import { useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  ArrowLeft,
  ExternalLink,
  Pencil,
  Plus,
  Trash2,
  AlertTriangle,
  ChevronDown,
  ChevronRight,
  ShieldAlert,
  Upload,
  FileText,
  Archive,
  RotateCcw,
  Zap,
  Info,
} from "lucide-react";
import apiClient from "@/api/client";
import { Badge } from "@/components/ui/Badge";
import { Button } from "@/components/ui/Button";
import { PageLoader } from "@/components/ui/Spinner";
import { Modal } from "@/components/ui/Modal";
import { FileDropZone } from "@/components/ui/FileDropZone";
import { ManagedFileLink, type ManagedFileRef } from "@/components/ui/ManagedFileLink";
import { DatePicker } from "@/components/ui/DatePicker";
import { useAuthStore } from "@/store/auth";
import { formatAmount } from "@/utils/formatAmount";
import { usePageTitle } from "@/hooks/usePageTitle";
import { canManageAgreementGroups, canManageContractors } from "@/features/contractors/utils";
import { AgreementGroupsSection } from "@/features/contractors/components/AgreementGroupsSection";
import { AgreementCoordinatorInfoSection } from "@/features/contractors/components/AgreementCoordinatorInfoSection";
import type { AgreementGroupBrief } from "@/features/contractors/agreementGroups";
import { AgreementHistorySection } from "@/features/contractors/components/AgreementHistorySection";
import { AgreementExtraFilesSection } from "@/features/contractors/components/AgreementExtraFilesSection";
import { AgreementConversations } from "@/features/contractors/components/AgreementConversations";
import {
  AcceptanceDeadlineDaysField,
  acceptanceDeadlineDaysError,
  acceptanceDeadlineDaysLabel,
} from "@/features/contractors/components/AcceptanceDeadlineDaysField";
import { useConversationDeepLink } from "@/hooks/useConversationDeepLink";

// ── Types ─────────────────────────────────────────────────────────────────────

type LineItemCategory = "" | "design_documentation";
const DESIGN_DOCUMENTATION: LineItemCategory = "design_documentation";

/** Lexical HINT for a freshly typed position name — mirrors the backend suggestion
 *  (design_contractors.suggest_category). It only pre-ticks the checkbox; the saved
 *  category is whatever the user leaves ticked. */
const DESIGN_NAME_HINT =
  /dokumentacj\w*\s+projektow\w*|projekt\w*\s+wykonawcz\w*|projekt\w*\s+techniczn\w*|\bpt\b|projekt\w*\s+(architektoniczno[\s-]*)?budowlan\w*|\bpab\b|\bpb\b|zagospodarowani\w*\s+teren\w*|\bpzt\b/;
const PL_FOLD: Record<string, string> = { ą: "a", ć: "c", ę: "e", ł: "l", ń: "n", ó: "o", ś: "s", ź: "z", ż: "z" };
const looksLikeDesignPosition = (name: string) => {
  const folded = name.toLowerCase().replace(/[ąćęłńóśźż]/g, (ch) => PL_FOLD[ch] ?? ch);
  return !/kosztorys\w*/.test(folded) && DESIGN_NAME_HINT.test(folded);
};

interface LineItemType {
  id: string;
  name: string;
  unit: string;
  base_rate: string;
  high_priority_rate: string | null;
  attachment_required: boolean;
  attachment_count_per_unit: boolean;
  // Kontrola zdjęć w plikach pozycji realizacyjnej (EXIF): zrzuty ekranu dozwolone /
  // wymagany geotag. Patrz apps/orders/photo_meta.py.
  allow_screenshots: boolean;
  geotag_required: boolean;
  // Kategoria strukturalna. „design_documentation” = firma jest proponowana przy zlecaniu
  // PT/PAB/PZT w projektach ZE — decyduje TO pole, nie brzmienie nazwy pozycji.
  category: LineItemCategory;
  category_label: string;
  // Wycofanie „w przód": pozycja zostaje na starych zamówieniach, ale nie da się jej
  // wziąć na nic nowego. is_active=false → wycofana.
  is_active: boolean;
  deactivated_at: string | null;
  deactivated_by_name: string | null;
  deactivation_note: string;
  // Pozycja automatyczna: dopisuje się sama do nowego zamówienia, gdy trafi na nie
  // którakolwiek z pozycji wyzwalających (auto_triggers) — zawsze raz. auto_quantity=null
  // → ilość jak w wyzwalaczu. auto_power_projects_only → tylko zamówienia projektów
  // energetycznych (DLC01).
  is_auto: boolean;
  auto_triggers: string[];
  auto_trigger_names: string[];
  /** Nazwy wyzwalaczy sklejone przecinkiem (zgodność). */
  auto_trigger_name: string | null;
  auto_quantity: string | null;
  auto_power_projects_only: boolean;
  auto_once_per_project: boolean;
  // Sposób wyceny: rate = ilość × stawka; lump_sum = pełna kwota (base_rate = cała kwota,
  // jednostka „kwota”, ilość 1); time_based = kwota zależna od dni od przyjęcia zamówienia
  // do zgłoszenia wykonania (progi rosnąco, po ostatnim progu 0 zł).
  auto_pricing_mode: AutoPricingMode;
  auto_time_tiers: AutoTimeTier[];
}
type AutoPricingMode = "rate" | "lump_sum" | "time_based";
interface AutoTimeTier { days: number; amount: string; }

interface PenaltyTerm {
  id: string;
  agreement: string;
  line_item_type: string | null;
  line_item_type_name: string | null;
  penalty_per_day_pct: string | null;
  penalty_per_day_amount: string | null;
  flat_penalty_amount: string | null;
  max_penalty_pct: string | null;
  max_penalty_amount: string | null;
  termination_after_days: number | null;
  termination_allowed: boolean;
  is_delay_penalty: boolean;
  notes: string;
}

interface PenaltyRecord {
  id: string;
  agreement: string;
  order: string | null;
  order_number: string | null;
  penalty_term: string | null;
  penalty_term_description: string | null;
  delay_days: number;
  amount: string;
  is_termination: boolean;
  note: string;
  applied_by_name: string | null;
  created_at: string;
}

interface AgreementFileVersion {
  id: string;
  kind: "document" | "pricing" | "insurance";
  created_at: string;
  managed_file: string | null;
  managed_file_detail: (ManagedFileRef & { id: string; original_filename: string }) | null;
  uploaded_by: string | null;
  uploaded_by_name: string | null;
}

interface Agreement {
  id: string;
  contractor: string;
  contractor_name: string;
  title: string;
  signed_date: string;
  valid_until: string | null;
  document_managed_file: string | null;
  document_managed_file_detail: { id: string; original_filename: string; content_type: string; size_bytes: number; url: string | null } | null;
  // Upload history of the agreement scan — served only to users who may replace it.
  document_file_versions: AgreementFileVersion[];
  // Cennik — visible to both parties, editable only by the director
  pricing_managed_file: string | null;
  pricing_managed_file_detail: (ManagedFileRef & { id: string; original_filename: string }) | null;
  is_confidential: boolean;
  auto_accept: boolean;
  payment_term_days: number | null;
  // Termin na przyjęcie zamówienia (dni od wysłania); null = domyślne 5 dni. Po terminie
  // nieprzyjęte zamówienie anuluje się samo (apps.orders.acceptance_deadline).
  acceptance_deadline_days?: number | null;
  // Kwota minimalna zamówienia — null = bez minimum. Ostrzeżenie (nie blokada) na
  // zapotrzebowaniach o niższej wartości.
  min_order_amount: string | null;
  allow_partial_pock: boolean;
  allow_external_costs: boolean;
  allow_official_fee_requests: boolean;
  line_item_types: LineItemType[];
  penalty_terms: PenaltyTerm[];
  // Grupy umów ramowych (np. regionalne/branżowe) — tylko do odczytu tutaj,
  // zmiana przez PUT /agreements/{id}/groups/. Backend pomija pole dla kont podwykonawcy.
  groups?: AgreementGroupBrief[];
  /** Notatka dla zamawiających (ikona „?" na megamapie). Backend pomija pole dla kont podwykonawcy. */
  coordinator_info?: string;
  // Autor umowy — obok dyrektora jedyna osoba, która może ją usunąć.
  created_by: string | null;
  created_by_name: string | null;
  // Wyliczane przez backend dla ZALOGOWANEGO użytkownika: czy wolno mu usunąć umowę
  // i (jeśli wolno) co blokuje usunięcie — umowa użyta na zamówieniach zostaje.
  can_delete: boolean;
  delete_blocked_reason: string | null;
  // Insurance (§13)
  requires_insurance: boolean;
  insurance_policy_managed_file: string | null;
  insurance_policy_managed_file_detail: { id: string; original_filename: string; content_type: string; size_bytes: number; url: string | null } | null;
  insurance_policy_file_url: string | null;
  insurance_sum: string | null;
  insurance_valid_until: string | null;
  min_insurance_sum: string | null;
  insurance_status:
    | "not_required"
    | "missing"
    | "expired"
    | "expiring_soon"
    | "valid";
  // Report template
  report_template: "standard" | "complex_lot";
  requires_weekly_reports: boolean | null;
  report_day_of_week: number | null;
  report_due_time: string | null;
}

interface OCPolicy {
  id: string;
  policy_file_url: string | null;
  insurance_sum: string;
  valid_until: string;
  uploaded_by_name: string | null;
  note: string;
  created_at: string;
}

interface AgreementSummary {
  total_orders_value: string;
  invoiced_net: string;
  paid_net: string;
  pending_net: string;
  penalties_total: string;
  orders_count: number;
  active_orders_count: number;
  // Ocena widoczna wyłącznie dla GK — akcja summary jest tylko dla dyrektora/CEO.
  rating: number | null;
  ratings_count: number;
}

interface Order {
  id: string;
  order_number: string;
  status: string;
  priority: string;
  deadline: string | null;
  project_name: string;
  approved_at: string;
}

interface Protocol {
  id: string;
  protocol_number: string;
  order_number: string;
  is_final: boolean;
  is_withdrawn: boolean;
  accepted_by_contractor: boolean | null;
  created_at: string;
}

interface Invoice {
  id: string;
  invoice_number: string;
  amount_net: string;
  amount_gross: string;
  status: string;
  payment_status: string;
  paid_amount: string;
  submitted_at: string;
  order_number: string;
}

// ── Constants ─────────────────────────────────────────────────────────────────

const ORDER_STATUS_LABEL: Record<string, string> = {
  sent: "Wysłane",
  accepted: "Przyjęte",
  rejected: "Odrzucone",
  in_progress: "W realizacji",
  in_progress_partial: "Realizacja częściowa",
  submitted_for_acceptance: "Do odbioru",
  accepted_final: "Zakończone",
  closed: "Zamknięte",
  cancelled: "Anulowane",
};
const ORDER_STATUS_COLOR: Record<
  string,
  "gray" | "blue" | "green" | "red" | "yellow"
> = {
  sent: "blue",
  accepted: "blue",
  rejected: "red",
  in_progress: "yellow",
  in_progress_partial: "yellow",
  submitted_for_acceptance: "blue",
  accepted_final: "green",
  closed: "gray",
  cancelled: "gray",
};
const INVOICE_STATUS_LABEL: Record<string, string> = {
  submitted: "Przesłana",
  accepted: "Przyjęta",
  rejected: "Nieprzyjęta",
  withdrawn: "Wycofana",
};
const INVOICE_STATUS_COLOR: Record<string, "gray" | "blue" | "green" | "red"> =
  {
    submitted: "blue",
    accepted: "green",
    rejected: "red",
    withdrawn: "gray",
  };
const PAYMENT_STATUS_LABEL: Record<string, string> = {
  unpaid: "Niezapłacona",
  partially_paid: "Częściowo",
  paid: "Zapłacona",
};
const PAYMENT_STATUS_COLOR: Record<string, "gray" | "yellow" | "green"> = {
  unpaid: "gray",
  partially_paid: "yellow",
  paid: "green",
};

// ── Summary cards ─────────────────────────────────────────────────────────────

// Polska liczba mnoga: 1 ocena, 2–4 oceny (poza 12–14), reszta ocen.
function pluralOcena(n: number): string {
  if (n === 1) return "ocena";
  const d = n % 10;
  const h = n % 100;
  return d >= 2 && d <= 4 && (h < 12 || h > 14) ? "oceny" : "ocen";
}

function SummaryCard({
  label,
  value,
  sub,
  color,
}: {
  label: string;
  value: string;
  sub?: string;
  color?: string;
}) {
  return (
    <div className="bg-white dark:bg-gray-800 p-4">
      <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">{label}</p>
      <p
        className={`text-xl font-bold ${color ?? "text-gray-900 dark:text-gray-100"}`}
      >
        {value}
      </p>
      {sub && (
        <p className="text-xs text-gray-400 dark:text-gray-500 mt-0.5">{sub}</p>
      )}
    </div>
  );
}

// ── Line items section ─────────────────────────────────────────────────────────

interface LineItemFormData {
  name: string;
  unit: string;
  base_rate: string;
  high_priority_rate: string;
  attachment_required: boolean;
  attachment_count_per_unit: boolean;
  allow_screenshots: boolean;
  geotag_required: boolean;
  category: LineItemCategory;
  /** True once the user has touched the category checkbox — the name-based hint then stops
   *  overriding their decision. */
  category_touched: boolean;
}
const emptyLineItemForm = (): LineItemFormData => ({
  name: "",
  unit: "",
  base_rate: "",
  high_priority_rate: "",
  attachment_required: false,
  attachment_count_per_unit: false,
  allow_screenshots: false,
  geotag_required: false,
  category: "",
  category_touched: false,
});
const lineItemToForm = (li: LineItemType): LineItemFormData => ({
  name: li.name,
  unit: li.unit,
  base_rate: li.base_rate,
  high_priority_rate: li.high_priority_rate ?? "",
  attachment_required: li.attachment_required,
  attachment_count_per_unit: li.attachment_count_per_unit,
  allow_screenshots: li.allow_screenshots,
  geotag_required: li.geotag_required,
  category: li.category ?? "",
  category_touched: true,
});

function LineItemRowActions({
  li,
  busy,
  onWithdraw,
  onRestore,
  onDelete,
}: {
  li: LineItemType;
  busy: boolean;
  onWithdraw: () => void;
  onRestore: () => void;
  onDelete: () => void;
}) {
  if (!li.is_active) {
    return (
      <button
        className="text-gray-400 hover:text-emerald-600 disabled:opacity-40 dark:text-gray-500"
        title="Przywróć pozycję do cennika"
        disabled={busy}
        onClick={onRestore}
      >
        <RotateCcw size={14} />
      </button>
    );
  }
  return (
    <>
      <button
        className="text-gray-400 hover:text-amber-600 disabled:opacity-40 dark:text-gray-500"
        title="Wycofaj — zostaje na starych zamówieniach, nie wejdzie na nowe"
        disabled={busy}
        onClick={onWithdraw}
      >
        <Archive size={14} />
      </button>
      <button
        className="text-gray-400 hover:text-red-600 disabled:opacity-40 dark:text-gray-500"
        title="Usuń bezpowrotnie (tylko pozycja jeszcze nigdzie nieużyta)"
        disabled={busy}
        onClick={() => {
          if (
            confirm(
              `Usunąć pozycję "${li.name}" bezpowrotnie?\n\nJeśli jest już na jakimkolwiek zapotrzebowaniu lub zamówieniu, system odmówi — wtedy wycofaj ją zamiast kasować.`,
            )
          ) {
            onDelete();
          }
        }}
      >
        <Trash2 size={14} />
      </button>
    </>
  );
}

function CategoryBadge({ li }: { li: LineItemType }) {
  if (li.category !== DESIGN_DOCUMENTATION) return null;
  return (
    <span
      className="ml-2 inline-flex items-center bg-sky-100 px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wide text-sky-800 dark:bg-sky-900/40 dark:text-sky-200"
      title="Pozycja dokumentacji projektowej — firma jest proponowana przy zlecaniu PT/PAB/PZT w projektach ZE."
    >
      Dok. projektowa
    </span>
  );
}

function WithdrawnBadge({ li }: { li: LineItemType }) {
  if (li.is_active) return null;
  const when = li.deactivated_at
    ? new Date(li.deactivated_at).toLocaleDateString("pl-PL")
    : null;
  return (
    <span
      className="ml-2 inline-flex items-center gap-1 bg-amber-100 px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wide text-amber-800 dark:bg-amber-900/40 dark:text-amber-200"
      title={
        [
          when ? `Wycofana ${when}` : "Wycofana",
          li.deactivated_by_name ? `przez: ${li.deactivated_by_name}` : null,
          li.deactivation_note ? `Powód: ${li.deactivation_note}` : null,
        ]
          .filter(Boolean)
          .join(" · ") + " — zostaje na starych zamówieniach, nie wchodzi na nowe"
      }
    >
      <Archive size={10} />
      Wycofana{when ? ` ${when}` : ""}
    </span>
  );
}

function LineItemsSection({
  agreementId,
  lineItems: allLineItems,
  isDirector,
}: {
  agreementId: string;
  lineItems: LineItemType[];
  isDirector: boolean;
}) {
  // Pozycje automatyczne mają własną sekcję (AutoLineItemsSection) pod cennikiem.
  const lineItems = allLineItems.filter((li) => !li.is_auto);
  const qc = useQueryClient();
  const [showAdd, setShowAdd] = useState(false);
  const [editId, setEditId] = useState<string | null>(null);
  const [form, setForm] = useState<LineItemFormData>(emptyLineItemForm());
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});

  const saveLineItem = async (payload: LineItemFormData, itemId?: string) => {
    const errs: Record<string, string> = {};
    if (!payload.name.trim()) errs.name = "Nazwa jest wymagana";
    if (!payload.unit.trim()) errs.unit = "Jednostka jest wymagana";
    if (!payload.base_rate || isNaN(parseFloat(payload.base_rate)))
      errs.base_rate = "Stawka bazowa jest wymagana";
    setFormErrors(errs);
    if (Object.keys(errs).length > 0) return;

    const body = {
      agreement: agreementId,
      name: payload.name,
      unit: payload.unit,
      base_rate: payload.base_rate,
      high_priority_rate: payload.high_priority_rate || null,
      attachment_required: payload.attachment_required,
      attachment_count_per_unit: payload.attachment_count_per_unit,
      allow_screenshots: payload.allow_screenshots,
      geotag_required: payload.geotag_required,
      category: payload.category,
    };
    try {
      if (itemId) {
        await apiClient.patch(
          `/agreements/${agreementId}/line-items/${itemId}/`,
          body,
        );
      } else {
        await apiClient.post(`/agreements/${agreementId}/line-items/`, body);
      }
      qc.invalidateQueries({ queryKey: ["agreement", agreementId] });
      setShowAdd(false);
      setEditId(null);
      setForm(emptyLineItemForm());
    } catch (e: unknown) {
      // Backend odmawia m.in. wyzwalacza spoza umowy albo łańcucha pozycji automatycznych —
      // pokaż jego komunikat zamiast ogólnego „błąd zapisu”.
      const errs = (e as { response?: { data?: { errors?: unknown } } })?.response?.data
        ?.errors;
      const msg =
        errs && typeof errs === "object"
          ? Object.values(errs as Record<string, unknown>)
              .flat()
              .map(String)
              .join(" ")
          : typeof errs === "string"
            ? errs
            : "";
      setFormErrors({ _: msg || "Błąd zapisu. Sprawdź dane." });
    }
  };

  const deleteMutation = useMutation({
    mutationFn: (itemId: string) =>
      apiClient.delete(`/agreements/${agreementId}/line-items/${itemId}/`),
    onSuccess: () =>
      qc.invalidateQueries({ queryKey: ["agreement", agreementId] }),
    // Backend odmawia skasowania pozycji, która jest już na jakimś zapotrzebowaniu lub
    // zamówieniu — komunikat mówi wprost, że od tego jest wycofanie.
    onError: (e: unknown) => {
      const detail = (e as { response?: { data?: { errors?: unknown } } })?.response
        ?.data?.errors;
      alert(
        typeof detail === "string"
          ? detail
          : "Nie udało się usunąć pozycji. Jeśli jest już użyta — wycofaj ją zamiast kasować.",
      );
    },
  });

  // Wycofanie zamiast usunięcia — patrz baner nad tabelą.
  const withdrawMutation = useMutation({
    mutationFn: ({
      itemId,
      withdraw,
      note,
    }: {
      itemId: string;
      withdraw: boolean;
      note?: string;
    }) =>
      apiClient.post(
        `/agreements/${agreementId}/line-items/${itemId}/${withdraw ? "deactivate" : "reactivate"}/`,
        { note: note ?? "" },
      ),
    onSuccess: () =>
      qc.invalidateQueries({ queryKey: ["agreement", agreementId] }),
  });

  const withdraw = (li: LineItemType) => {
    const note = prompt(
      `Wycofać pozycję "${li.name}"?\n\nZostanie na dotychczasowych zapotrzebowaniach i zamówieniach, ale nie da się jej wybrać w nowych.\n\nPowód / numer aneksu (opcjonalnie):`,
    );
    if (note === null) return;
    withdrawMutation.mutate({ itemId: li.id, withdraw: true, note });
  };

  const restore = (li: LineItemType) => {
    if (confirm(`Przywrócić pozycję "${li.name}" do cennika?`)) {
      withdrawMutation.mutate({ itemId: li.id, withdraw: false });
    }
  };

  const activeItems = lineItems.filter((li) => li.is_active);
  const withdrawnItems = lineItems.filter((li) => !li.is_active);

  const upd = <K extends keyof LineItemFormData>(k: K, v: string | boolean) =>
    setForm((p) => {
      const next = { ...p, [k]: v };
      // Name-based hint: pre-tick „Dokumentacja projektowa” while the user has not decided
      // the category themselves (new position). Never touches an explicit choice.
      if (k === "name" && !p.category_touched) {
        next.category = looksLikeDesignPosition(String(v)) ? DESIGN_DOCUMENTATION : "";
      }
      if (k === "category") next.category_touched = true;
      return next;
    });

  return (
    <section className="mb-6">
      <div className="flex items-center justify-between mb-3">
        <h2 className="text-base font-semibold text-gray-900 dark:text-gray-100">
          Pozycje realizacyjne ({activeItems.length})
          {withdrawnItems.length > 0 && (
            <span className="ml-1 font-normal text-gray-400 dark:text-gray-500">
              + {withdrawnItems.length} wycofane
            </span>
          )}
        </h2>
        {isDirector && (
          <button
            className="inline-flex items-center gap-1 text-sm text-[var(--color-primary)] hover:underline"
            onClick={() => {
              setShowAdd(true);
              setForm(emptyLineItemForm());
              setFormErrors({});
            }}
          >
            <Plus size={14} /> Dodaj pozycję
          </button>
        )}
      </div>

      {isDirector && withdrawnItems.length > 0 && (
        <div className="mb-3 flex items-start gap-2 border-l-2 border-amber-400 bg-amber-50 px-3 py-2 text-xs text-amber-900 dark:bg-amber-900/20 dark:text-amber-200">
          <Archive size={14} className="mt-0.5 shrink-0" />
          <span>
            Pozycje wycofane zostają na zapotrzebowaniach, zamówieniach i POCK-ach, na
            których już są — z dotychczasową nazwą i stawką. Nie da się ich wybrać w
            niczym nowym: ani w zapotrzebowaniu, ani przy dokładaniu pozycji do
            zamówienia, ani w wycenie. Podwykonawca ich nie widzi.
          </span>
        </div>
      )}

      {showAdd && (
        <LineItemForm
          form={form}
          errors={formErrors}
          upd={upd}
          onSave={() => saveLineItem(form)}
          onCancel={() => {
            setShowAdd(false);
            setFormErrors({});
          }}
          title="Nowa pozycja realizacyjna"
        />
      )}

      {lineItems.length === 0 ? (
        <p className="text-sm text-gray-400 dark:text-gray-500">
          Brak pozycji realizacyjnych.
        </p>
      ) : (
        <div className="bg-white dark:bg-gray-800 overflow-hidden">
          {/* Mobile cards */}
          <div className="sm:hidden divide-y divide-gray-100 dark:divide-gray-700">
            {[...activeItems, ...withdrawnItems].map((li) =>
              editId === li.id ? (
                <div key={li.id} className="px-3 py-3">
                  <LineItemForm
                    form={form}
                    errors={formErrors}
                    upd={upd}
                              onSave={() => saveLineItem(form, li.id)}
                    onCancel={() => {
                      setEditId(null);
                      setFormErrors({});
                    }}
                    title="Edytuj pozycję"
                  />
                </div>
              ) : (
                <div
                  key={li.id}
                  className={`px-3 py-3 ${li.is_active ? "" : "bg-gray-50/70 dark:bg-gray-900/30"}`}
                >
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex-1 min-w-0">
                      <p
                        className={`text-sm font-medium leading-snug ${li.is_active ? "text-gray-900 dark:text-gray-100" : "text-gray-400 line-through dark:text-gray-500"}`}
                      >
                        {li.name}
                      </p>
                      <CategoryBadge li={li} />
                      <WithdrawnBadge li={li} />
                      <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                        {li.unit}
                      </p>
                    </div>
                    {isDirector && (
                      <div className="flex items-center gap-2 shrink-0">
                        <button
                          className="text-gray-400 dark:text-gray-500 hover:text-[var(--color-primary)]"
                          title="Edytuj"
                          onClick={() => {
                            setEditId(li.id);
                            setForm(lineItemToForm(li));
                            setFormErrors({});
                            setShowAdd(false);
                          }}
                        >
                          <Pencil size={14} />
                        </button>
                        <LineItemRowActions
                          li={li}
                          busy={
                            deleteMutation.isPending || withdrawMutation.isPending
                          }
                          onWithdraw={() => withdraw(li)}
                          onRestore={() => restore(li)}
                          onDelete={() => deleteMutation.mutate(li.id)}
                        />
                      </div>
                    )}
                  </div>
                  <div className="mt-2 grid grid-cols-2 gap-x-4 gap-y-1 text-xs">
                    <div>
                      <span className="text-gray-400 dark:text-gray-500 uppercase tracking-wide text-[10px]">Stawka bazowa</span>
                      <p className="text-gray-700 dark:text-gray-300 font-medium">{formatAmount(li.base_rate)} PLN</p>
                    </div>
                    <div>
                      <span className="text-gray-400 dark:text-gray-500 uppercase tracking-wide text-[10px]">Wysoki priorytet</span>
                      <p className="text-gray-500 dark:text-gray-400">
                        {li.high_priority_rate ? `${formatAmount(li.high_priority_rate)} PLN` : "—"}
                      </p>
                    </div>
                    <div className="col-span-2">
                      <span className="text-gray-400 dark:text-gray-500 uppercase tracking-wide text-[10px]">Załącznik</span>
                      <p>
                        {li.attachment_required ? (
                          <span className="text-[var(--color-primary)]">
                            Wymagany
                            {" · "}
                            <span className="text-gray-500 dark:text-gray-400">
                              {li.attachment_count_per_unit ? "ilość = ilość pozycji" : "1 na pozycję"}
                            </span>
                          </span>
                        ) : (
                          <span className="text-gray-400 dark:text-gray-500">Niewymagany</span>
                        )}
                      </p>
                    </div>
                    {(li.geotag_required || li.allow_screenshots) && (
                      <div className="col-span-2">
                        <span className="text-gray-400 dark:text-gray-500 uppercase tracking-wide text-[10px]">Kontrola zdjęć</span>
                        <p className="text-gray-500 dark:text-gray-400">
                          {[
                            li.geotag_required ? "wymagany geotag" : null,
                            li.allow_screenshots ? "zrzuty ekranu dozwolone" : null,
                          ]
                            .filter(Boolean)
                            .join(" · ")}
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              )
            )}
          </div>

          {/* Desktop table */}
          <table className="hidden sm:table w-full text-xs">
            <thead className="bg-gray-50 dark:bg-gray-800/50 border-b border-gray-200 dark:border-gray-700">
              <tr>
                <th className="text-left px-4 py-2 font-mono text-xs uppercase tracking-widest text-gray-500 dark:text-gray-400">
                  Nazwa
                </th>
                <th className="text-left px-4 py-2 font-mono text-xs uppercase tracking-widest text-gray-500 dark:text-gray-400">
                  Jm.
                </th>
                <th className="text-right px-4 py-2 font-mono text-xs uppercase tracking-widest text-gray-500 dark:text-gray-400">
                  Stawka bazowa
                </th>
                <th className="text-right px-4 py-2 font-mono text-xs uppercase tracking-widest text-gray-500 dark:text-gray-400">
                  Stawka wysoki priorytet
                </th>
                <th className="text-center px-4 py-2 font-mono text-xs uppercase tracking-widest text-gray-500 dark:text-gray-400">
                  Załącznik
                </th>
                {isDirector && <th className="px-4 py-2 font-mono text-xs uppercase tracking-widest text-gray-500 dark:text-gray-400" />}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {[...activeItems, ...withdrawnItems].map((li) =>
                editId === li.id ? (
                  <tr key={li.id}>
                    <td colSpan={isDirector ? 6 : 5} className="px-4 py-3">
                      <LineItemForm
                        form={form}
                        errors={formErrors}
                        upd={upd}
                                      onSave={() => saveLineItem(form, li.id)}
                        onCancel={() => {
                          setEditId(null);
                          setFormErrors({});
                        }}
                        title="Edytuj pozycję"
                      />
                    </td>
                  </tr>
                ) : (
                  <tr
                    key={li.id}
                    className={`hover:bg-gray-50 dark:hover:bg-gray-700/50 ${li.is_active ? "" : "bg-gray-50/70 dark:bg-gray-900/30"}`}
                  >
                    <td className="px-4 py-3 font-medium">
                      <span
                        className={
                          li.is_active
                            ? "text-gray-900 dark:text-gray-100"
                            : "text-gray-400 line-through dark:text-gray-500"
                        }
                      >
                        {li.name}
                      </span>
                      <CategoryBadge li={li} />
                      <WithdrawnBadge li={li} />
                    </td>
                    <td className="px-4 py-3 text-gray-500 dark:text-gray-400">
                      {li.unit}
                    </td>
                    <td className="px-4 py-3 text-right text-gray-700 dark:text-gray-300">
                      {formatAmount(li.base_rate)} PLN
                    </td>
                    <td className="px-4 py-3 text-right text-gray-500 dark:text-gray-400">
                      {li.high_priority_rate
                        ? `${formatAmount(li.high_priority_rate)} PLN`
                        : "—"}
                    </td>
                    <td className="px-4 py-3 text-center">
                      {li.attachment_required ? (
                        <span className="inline-flex flex-col items-center gap-0.5">
                          <span className="text-xs font-medium text-[var(--color-primary)] dark:text-[var(--color-primary)]">
                            Wymagany
                          </span>
                          <span className="text-[10px] text-gray-500 dark:text-gray-400">
                            {li.attachment_count_per_unit
                              ? "ilość = ilość pozycji"
                              : "1 na pozycję"}
                          </span>
                        </span>
                      ) : (
                        <span className="text-xs text-gray-400 dark:text-gray-500">
                          Niewymagany
                        </span>
                      )}
                      {(li.geotag_required || li.allow_screenshots) && (
                        <span className="mt-0.5 block text-[10px] text-gray-500 dark:text-gray-400">
                          {[
                            li.geotag_required ? "wymagany geotag" : null,
                            li.allow_screenshots ? "zrzuty ekranu dozwolone" : null,
                          ]
                            .filter(Boolean)
                            .join(" · ")}
                        </span>
                      )}
                    </td>
                    {isDirector && (
                      <td className="px-4 py-3 text-right">
                        <div className="flex items-center justify-end gap-2">
                          <button
                            className="text-gray-400 dark:text-gray-500 hover:text-[var(--color-primary)]"
                            title="Edytuj"
                            onClick={() => {
                              setEditId(li.id);
                              setForm(lineItemToForm(li));
                              setFormErrors({});
                              setShowAdd(false);
                            }}
                          >
                            <Pencil size={14} />
                          </button>
                          <LineItemRowActions
                            li={li}
                            busy={
                              deleteMutation.isPending || withdrawMutation.isPending
                            }
                            onWithdraw={() => withdraw(li)}
                            onRestore={() => restore(li)}
                            onDelete={() => deleteMutation.mutate(li.id)}
                          />
                        </div>
                      </td>
                    )}
                  </tr>
                ),
              )}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}

function LineItemForm({
  form,
  errors,
  upd,
  onSave,
  onCancel,
  title,
}: {
  form: LineItemFormData;
  errors: Record<string, string>;
  upd: (k: keyof LineItemFormData, v: string | boolean) => void;
  onSave: () => void;
  onCancel: () => void;
  title: string;
}) {
  return (
    <div className="mb-4 rounded-xl border border-[var(--color-primary)] bg-[var(--color-primary-dim)] dark:bg-[var(--color-primary-hover)] p-4">
      <p className="text-sm font-semibold text-[var(--color-primary)] mb-3">{title}</p>
      <div className="grid grid-cols-2 gap-3 mb-3">
        <div className="col-span-2">
          <label className="text-xs font-medium text-gray-700 dark:text-gray-300 block mb-1">
            Nazwa<span className="text-red-500 ml-0.5">*</span>
          </label>
          <input
            type="text"
            value={form.name}
            onChange={(e) => upd("name", e.target.value)}
            placeholder='np. "Pierwsze 100m MdcP"'
            className={`block w-full rounded-md border px-3 py-1.5 text-sm ${errors.name ? "border-red-400" : "border-gray-300"}`}
          />
          {errors.name && (
            <p className="text-xs text-red-600 mt-0.5">{errors.name}</p>
          )}
        </div>
        <div>
          <label className="text-xs font-medium text-gray-700 dark:text-gray-300 block mb-1">
            Jednostka<span className="text-red-500 ml-0.5">*</span>
          </label>
          <input
            type="text"
            value={form.unit}
            onChange={(e) => upd("unit", e.target.value)}
            placeholder='np. "szt.", "mb"'
            className={`block w-full rounded-md border px-3 py-1.5 text-sm ${errors.unit ? "border-red-400" : "border-gray-300"}`}
          />
          {errors.unit && (
            <p className="text-xs text-red-600 mt-0.5">{errors.unit}</p>
          )}
        </div>
        <div>
          <label className="text-xs font-medium text-gray-700 dark:text-gray-300 block mb-1">
            Stawka bazowa [PLN]<span className="text-red-500 ml-0.5">*</span>
          </label>
          <input
            type="number"
            min="0"
            step="0.01"
            value={form.base_rate}
            onChange={(e) => upd("base_rate", e.target.value)}
            className={`block w-full rounded-md border px-3 py-1.5 text-sm ${errors.base_rate ? "border-red-400" : "border-gray-300"}`}
          />
          {errors.base_rate && (
            <p className="text-xs text-red-600 mt-0.5">{errors.base_rate}</p>
          )}
        </div>
        <div>
          <label className="text-xs font-medium text-gray-700 dark:text-gray-300 block mb-1">
            Stawka wysoki priorytet [PLN] (opcjonalnie)
          </label>
          <input
            type="number"
            min="0"
            step="0.01"
            value={form.high_priority_rate}
            onChange={(e) => upd("high_priority_rate", e.target.value)}
            className="block w-full rounded-md border border-gray-300 px-3 py-1.5 text-sm"
          />
        </div>
      </div>
      <div className="flex flex-wrap gap-4 mt-1 mb-3">
        <label className="flex items-center gap-2 cursor-pointer select-none">
          <input
            type="checkbox"
            checked={form.attachment_required}
            onChange={(e) => upd("attachment_required", e.target.checked)}
            className="rounded border-gray-300"
          />
          <span className="text-sm text-gray-700 dark:text-gray-300">
            Wymagany załącznik przy POCK
          </span>
        </label>
        {form.attachment_required && (
          <label className="flex items-center gap-2 cursor-pointer select-none">
            <input
              type="checkbox"
              checked={form.attachment_count_per_unit}
              onChange={(e) =>
                upd("attachment_count_per_unit", e.target.checked)
              }
              className="rounded border-gray-300"
            />
            <span className="text-sm text-gray-700 dark:text-gray-300">
              Liczba załączników = liczba sztuk (1 zał. / szt.)
            </span>
          </label>
        )}
      </div>
      {/* Kontrola zdjęć w plikach pozycji realizacyjnej (EXIF czytany przy uploadzie). */}
      <div className="flex flex-wrap gap-4 mt-1 mb-3">
        <label
          className="flex items-center gap-2 cursor-pointer select-none"
          title="Zdjęcia przesyłane jako pliki pozycji realizacyjnej powinny mieć współrzędne GPS (EXIF). Brak geotagu to ostrzeżenie, nie blokada."
        >
          <input
            type="checkbox"
            checked={form.geotag_required}
            onChange={(e) => upd("geotag_required", e.target.checked)}
            className="rounded border-gray-300"
          />
          <span className="text-sm text-gray-700 dark:text-gray-300">
            Wymagany geotag na zdjęciach
          </span>
        </label>
        <label
          className="flex items-center gap-2 cursor-pointer select-none"
          title="Wyłącza automatyczne odrzucanie zrzutów ekranu — dla pozycji, których produktem jest zrzut (potwierdzenia z portali, wydruki z geoportalu, raporty)."
        >
          <input
            type="checkbox"
            checked={form.allow_screenshots}
            onChange={(e) => upd("allow_screenshots", e.target.checked)}
            className="rounded border-gray-300"
          />
          <span className="text-sm text-gray-700 dark:text-gray-300">
            Zrzuty ekranu dozwolone jako plik wynikowy
          </span>
        </label>
      </div>
      {/* Kategoria strukturalna — od niej (nie od nazwy) zależy, czy firma pojawi się
          na liście projektantów przy zlecaniu PT/PAB/PZT w projekcie ZE. */}
      <div className="flex flex-wrap gap-4 mt-1 mb-3">
        <label
          className="flex items-center gap-2 cursor-pointer select-none"
          title="Zaznacz dla pozycji na opracowania projektowe (PT / PAB / PZT). Tylko firmy z taką pozycją są proponowane przy zlecaniu projektu technicznego w projektach ZE — brzmienie nazwy pozycji nie ma znaczenia."
        >
          <input
            type="checkbox"
            checked={form.category === DESIGN_DOCUMENTATION}
            onChange={(e) => upd("category", e.target.checked ? DESIGN_DOCUMENTATION : "")}
            className="rounded border-gray-300"
          />
          <span className="text-sm text-gray-700 dark:text-gray-300">
            Dokumentacja projektowa (PT / PAB / PZT) — firma proponowana przy zlecaniu projektu w ZE
          </span>
        </label>
        {!form.category_touched && form.category === DESIGN_DOCUMENTATION && (
          <span className="text-xs text-[var(--color-text-muted)]">
            zaznaczono automatycznie na podstawie nazwy — odznacz, jeśli to nie jest pozycja projektowa
          </span>
        )}
      </div>
      {errors._ && <p className="text-xs text-red-600 mb-2">{errors._}</p>}
      <div className="flex gap-2">
        <Button size="sm" onClick={onSave}>
          Zapisz
        </Button>
        <Button size="sm" variant="secondary" onClick={onCancel}>
          Anuluj
        </Button>
      </div>
    </div>
  );
}

// ── Automatic line items section ───────────────────────────────────────────────
// Pozycja automatyczna nie jest wybierana ręcznie: dopisuje się sama do NOWEGO zamówienia,
// gdy trafi na nie wskazana pozycja wyzwalająca (backend: OrderService.append_auto_line_items).

const POWER_ONLY_TIP =
  "Zaznaczone: pozycja dopisuje się tylko do zamówień w projektach energetycznych, czyli zlecanych " +
  "z modułu DLC01 (projekt techniczny, kosztorys, MDCP, Wody Polskie, WPW). Odznaczone: dopisuje się " +
  "do każdego zamówienia, na którym pojawi się pozycja wyzwalająca — także z klasycznego zapotrzebowania.";
const RATE_TIP =
  "Pozycja liczy się jak w cenniku: ilość × stawka. Ilość bierze się z pozycji wyzwalającej albo " +
  "z pola „Ilość” niżej.";
const LUMP_SUM_TIP =
  "Pozycja to jedna kwota (np. premia), nie „ilość × stawka”. Podajesz tylko pełną kwotę — " +
  "jednostka, stawka i ilość znikają, a na zamówieniu pozycja pojawia się raz z tą kwotą.";
const TIME_BASED_TIP =
  "Kwota zależy od tempa realizacji. Podajesz progi: „do N dni od przyjęcia zamówienia → kwota”. " +
  "Liczy się liczba dni od przyjęcia zamówienia przez podwykonawcę do zgłoszenia wykonania " +
  "(wniosek o POCK / POCK końcowy). Obowiązuje kwota pierwszego progu, którego nie przekroczono; " +
  "po ostatnim progu 0 zł. Na zamówieniu pozycja startuje z kwotą pierwszego progu i jest " +
  "przeliczana raz, przy zgłoszeniu wykonania.";
const PRICING_MODES: { value: AutoPricingMode; label: string; tip: string }[] = [
  { value: "rate", label: "Ilość × stawka", tip: RATE_TIP },
  { value: "lump_sum", label: "Pełna kwota", tip: LUMP_SUM_TIP },
  { value: "time_based", label: "Zmienna w czasie", tip: TIME_BASED_TIP },
];
const tiersSummary = (tiers: AutoTimeTier[]) =>
  tiers.map((t) => `do ${t.days} dni: ${formatAmount(t.amount)} zł`).join(" · ") + " · potem 0 zł";
const ONCE_PER_PROJECT_TIP =
  "Zaznaczone: pozycja dopisuje się tylko do PIERWSZEGO zamówienia w danym przedsięwzięciu. Kolejne " +
  "zamówienia tego samego przedsięwzięcia z pozycją wyzwalającą już jej nie dostają (zamówienia " +
  "odrzucone, anulowane i usunięte nie liczą się). Odznaczone: dopisuje się do każdego zamówienia " +
  "z pozycją wyzwalającą.";

interface AutoLineItemFormData {
  name: string;
  unit: string;
  base_rate: string;
  high_priority_rate: string;
  /** Pozycje wyzwalające — co najmniej jedna. */
  auto_triggers: string[];
  /** Pusty string = ilość jak w pozycji wyzwalającej. */
  auto_quantity: string;
  auto_power_projects_only: boolean;
  auto_once_per_project: boolean;
  auto_pricing_mode: AutoPricingMode;
  /** Wiersze progów jako tekst (walidacja przy zapisie). */
  auto_time_tiers: { days: string; amount: string }[];
}
const emptyAutoLineItemForm = (): AutoLineItemFormData => ({
  name: "",
  unit: "",
  base_rate: "",
  high_priority_rate: "",
  auto_triggers: [],
  auto_quantity: "",
  auto_power_projects_only: true,
  auto_once_per_project: true,
  auto_pricing_mode: "rate",
  auto_time_tiers: [{ days: "", amount: "" }],
});
const autoLineItemToForm = (li: LineItemType): AutoLineItemFormData => ({
  name: li.name,
  unit: li.unit,
  base_rate: li.base_rate,
  high_priority_rate: li.high_priority_rate ?? "",
  auto_triggers: li.auto_triggers ?? [],
  auto_quantity: li.auto_quantity ?? "",
  auto_power_projects_only: li.auto_power_projects_only ?? true,
  auto_once_per_project: li.auto_once_per_project ?? true,
  auto_pricing_mode: li.auto_pricing_mode ?? "rate",
  auto_time_tiers:
    li.auto_time_tiers && li.auto_time_tiers.length > 0
      ? li.auto_time_tiers.map((t) => ({ days: String(t.days), amount: t.amount }))
      : [{ days: "", amount: "" }],
});

/** Nazwy pozycji wyzwalających — kilka = „którakolwiek z nich, raz”. */
function AutoTriggerNames({ li }: { li: LineItemType }) {
  const names = li.auto_trigger_names ?? [];
  if (names.length === 0) return <span className="text-amber-600">brak wyzwalacza</span>;
  if (names.length === 1) return <>{names[0]}</>;
  return (
    <span
      className="inline-flex flex-wrap gap-1"
      title="Pozycja dopisuje się, gdy na zamówieniu znajdzie się którakolwiek z tych pozycji (raz)."
    >
      {names.map((n) => (
        <span
          key={n}
          className="rounded bg-gray-100 px-1.5 py-0.5 text-[11px] text-gray-700 dark:bg-gray-700 dark:text-gray-200"
        >
          {n}
        </span>
      ))}
    </span>
  );
}

function AutoScopeBadges({ li }: { li: LineItemType }) {
  return (
    <span className="inline-flex flex-wrap gap-1">
      {li.auto_pricing_mode === "lump_sum" && (
        <span
          className="rounded bg-emerald-50 px-1.5 py-0.5 text-[10px] font-medium text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-200"
          title={LUMP_SUM_TIP}
        >
          pełna kwota
        </span>
      )}
      {li.auto_pricing_mode === "time_based" && (
        <span
          className="rounded bg-violet-50 px-1.5 py-0.5 text-[10px] font-medium text-violet-700 dark:bg-violet-900/30 dark:text-violet-200"
          title={`${TIME_BASED_TIP}\n\nProgi: ${tiersSummary(li.auto_time_tiers ?? [])}`}
        >
          zmienna w czasie
        </span>
      )}
      <span
        className={`rounded px-1.5 py-0.5 text-[10px] font-medium ${
          li.auto_power_projects_only
            ? "bg-sky-50 text-sky-700 dark:bg-sky-900/30 dark:text-sky-200"
            : "bg-gray-100 text-gray-500 dark:bg-gray-700 dark:text-gray-300"
        }`}
        title={POWER_ONLY_TIP}
      >
        {li.auto_power_projects_only ? "tylko ZE" : "każdy projekt"}
      </span>
      <span
        className={`rounded px-1.5 py-0.5 text-[10px] font-medium ${
          li.auto_once_per_project
            ? "bg-sky-50 text-sky-700 dark:bg-sky-900/30 dark:text-sky-200"
            : "bg-gray-100 text-gray-500 dark:bg-gray-700 dark:text-gray-300"
        }`}
        title={ONCE_PER_PROJECT_TIP}
      >
        {li.auto_once_per_project ? "raz na przedsięwzięcie" : "każde zamówienie"}
      </span>
    </span>
  );
}

function AutoLineItemsSection({
  agreementId,
  lineItems: allLineItems,
  isDirector,
}: {
  agreementId: string;
  lineItems: LineItemType[];
  isDirector: boolean;
}) {
  const qc = useQueryClient();
  const [showAdd, setShowAdd] = useState(false);
  const [editId, setEditId] = useState<string | null>(null);
  const [form, setForm] = useState<AutoLineItemFormData>(emptyAutoLineItemForm());
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});

  const autoItems = allLineItems.filter((li) => li.is_auto);
  const activeItems = autoItems.filter((li) => li.is_active);
  const withdrawnItems = autoItems.filter((li) => !li.is_active);
  // Wyzwalaczem może być tylko zwykła (nieautomatyczna), aktywna pozycja tej umowy.
  // Wycofany wyzwalacz, który edytowana pozycja wciąż ma zaznaczony, zostaje na liście
  // (z dopiskiem), żeby dało się go świadomie odznaczyć.
  const triggerOptions = allLineItems
    .filter((li) => !li.is_auto && (li.is_active || form.auto_triggers.includes(li.id)))
    .map((li) => ({
      value: li.id,
      label: `${li.name} (${li.unit})${li.is_active ? "" : " — wycofana"}`,
    }));

  const invalidate = () => qc.invalidateQueries({ queryKey: ["agreement", agreementId] });

  const save = async (payload: AutoLineItemFormData, itemId?: string) => {
    const errs: Record<string, string> = {};
    const mode = payload.auto_pricing_mode;
    if (!payload.name.trim()) errs.name = "Nazwa jest wymagana";
    if (mode === "rate" && !payload.unit.trim()) errs.unit = "Jednostka jest wymagana";
    if (mode !== "time_based" && (!payload.base_rate || isNaN(parseFloat(payload.base_rate))))
      errs.base_rate = mode === "lump_sum" ? "Kwota jest wymagana" : "Stawka bazowa jest wymagana";
    if (payload.auto_triggers.length === 0)
      errs.auto_triggers = "Wybierz co najmniej jedną pozycję, przy której ma się dodawać";
    if (
      mode === "rate" &&
      payload.auto_quantity.trim() !== "" &&
      !(parseFloat(payload.auto_quantity) > 0)
    )
      errs.auto_quantity = "Ilość musi być większa od zera (albo zostaw puste)";
    let tiers: { days: number; amount: string }[] = [];
    if (mode === "time_based") {
      const rows = payload.auto_time_tiers.filter((t) => t.days.trim() || t.amount.trim());
      if (rows.length === 0) errs.auto_time_tiers = "Podaj co najmniej jeden próg";
      let last = 0;
      for (const [i, t] of rows.entries()) {
        const days = parseInt(t.days, 10);
        const amount = parseFloat(t.amount.replace(",", "."));
        if (!(days > 0)) errs.auto_time_tiers = `Próg ${i + 1}: liczba dni musi być większa od zera`;
        else if (days <= last) errs.auto_time_tiers = `Próg ${i + 1}: progi muszą rosnąć`;
        else if (isNaN(amount) || amount < 0) errs.auto_time_tiers = `Próg ${i + 1}: podaj kwotę`;
        last = days;
        tiers.push({ days, amount: t.amount.replace(",", ".") });
      }
      if (errs.auto_time_tiers) tiers = [];
    }
    setFormErrors(errs);
    if (Object.keys(errs).length > 0) return;

    const body = {
      agreement: agreementId,
      name: payload.name,
      // Przy pełnej kwocie / zmiennej w czasie backend sam przypina jednostkę „kwota”,
      // zeruje ilość i stawkę wys.; przy zmiennej w czasie base_rate = kwota 1. progu.
      unit: mode === "rate" ? payload.unit : "",
      base_rate: mode === "time_based" ? tiers[0]?.amount ?? null : payload.base_rate,
      high_priority_rate: mode === "rate" ? payload.high_priority_rate || null : null,
      auto_triggers: payload.auto_triggers,
      auto_quantity:
        mode === "rate" && payload.auto_quantity.trim() !== "" ? payload.auto_quantity : null,
      auto_power_projects_only: payload.auto_power_projects_only,
      auto_once_per_project: payload.auto_once_per_project,
      auto_pricing_mode: mode,
      auto_time_tiers: tiers,
    };
    try {
      if (itemId) {
        await apiClient.patch(`/agreements/${agreementId}/line-items/${itemId}/`, body);
      } else {
        await apiClient.post(`/agreements/${agreementId}/line-items/`, body);
      }
      invalidate();
      setShowAdd(false);
      setEditId(null);
      setForm(emptyAutoLineItemForm());
    } catch (e: unknown) {
      // Backend odmawia m.in. wyzwalacza spoza umowy albo łańcucha pozycji automatycznych —
      // pokaż jego komunikat zamiast ogólnego „błąd zapisu”.
      const errs = (e as { response?: { data?: { errors?: unknown } } })?.response?.data?.errors;
      const msg =
        errs && typeof errs === "object"
          ? Object.values(errs as Record<string, unknown>).flat().map(String).join(" ")
          : typeof errs === "string"
            ? errs
            : "";
      setFormErrors({ _: msg || "Błąd zapisu. Sprawdź dane." });
    }
  };

  const deleteMutation = useMutation({
    mutationFn: (itemId: string) =>
      apiClient.delete(`/agreements/${agreementId}/line-items/${itemId}/`),
    onSuccess: invalidate,
    onError: (e: unknown) => {
      const detail = (e as { response?: { data?: { errors?: unknown } } })?.response?.data
        ?.errors;
      alert(
        typeof detail === "string"
          ? detail
          : "Nie udało się usunąć pozycji. Jeśli jest już użyta — wycofaj ją zamiast kasować.",
      );
    },
  });
  const withdrawMutation = useMutation({
    mutationFn: ({ itemId, withdraw, note }: { itemId: string; withdraw: boolean; note?: string }) =>
      apiClient.post(
        `/agreements/${agreementId}/line-items/${itemId}/${withdraw ? "deactivate" : "reactivate"}/`,
        { note: note ?? "" },
      ),
    onSuccess: invalidate,
  });
  const withdraw = (li: LineItemType) => {
    const note = prompt(
      `Wycofać pozycję automatyczną "${li.name}"?\n\nPrzestanie się dopisywać do nowych zamówień; na dotychczasowych zostaje.\n\nPowód / numer aneksu (opcjonalnie):`,
    );
    if (note === null) return;
    withdrawMutation.mutate({ itemId: li.id, withdraw: true, note });
  };
  const restore = (li: LineItemType) => {
    if (confirm(`Przywrócić pozycję automatyczną "${li.name}"?`)) {
      withdrawMutation.mutate({ itemId: li.id, withdraw: false });
    }
  };

  const upd = (k: keyof AutoLineItemFormData, v: string | boolean | string[]) =>
    setForm((p) => ({ ...p, [k]: v }));
  const onTierChange = (i: number, k: "days" | "amount", v: string) =>
    setForm((p) => ({
      ...p,
      auto_time_tiers: p.auto_time_tiers.map((t, j) => (j === i ? { ...t, [k]: v } : t)),
    }));
  const onTierAdd = () =>
    setForm((p) => ({ ...p, auto_time_tiers: [...p.auto_time_tiers, { days: "", amount: "" }] }));
  const onTierRemove = (i: number) =>
    setForm((p) => ({ ...p, auto_time_tiers: p.auto_time_tiers.filter((_, j) => j !== i) }));

  const startEdit = (li: LineItemType) => {
    setEditId(li.id);
    setForm(autoLineItemToForm(li));
    setFormErrors({});
    setShowAdd(false);
  };
  const cancelEdit = () => {
    setEditId(null);
    setFormErrors({});
  };

  const busy = deleteMutation.isPending || withdrawMutation.isPending;
  const quantityLabel = (li: LineItemType) =>
    li.auto_pricing_mode !== "rate"
      ? "1 (kwota)"
      : li.auto_quantity
        ? formatAmount(li.auto_quantity)
        : "jak w pozycji wyzwalającej";
  const unitLabel = (li: LineItemType) => (li.auto_pricing_mode !== "rate" ? "—" : li.unit);
  const rateLabel = (li: LineItemType) =>
    li.auto_pricing_mode === "time_based"
      ? tiersSummary(li.auto_time_tiers ?? [])
      : li.auto_pricing_mode === "lump_sum"
        ? `pełna kwota: ${formatAmount(li.base_rate)} PLN`
        : `${li.unit} · ${formatAmount(li.base_rate)} PLN`;

  return (
    <section className="mb-6">
      <div className="flex items-center justify-between mb-3">
        <h2 className="flex items-center gap-1.5 text-base font-semibold text-gray-900 dark:text-gray-100">
          <Zap size={16} className="text-sky-600" />
          Pozycje automatyczne ({activeItems.length})
          {withdrawnItems.length > 0 && (
            <span className="ml-1 font-normal text-gray-400 dark:text-gray-500">
              + {withdrawnItems.length} wycofane
            </span>
          )}
        </h2>
        {isDirector && (
          <button
            className="inline-flex items-center gap-1 text-sm text-[var(--color-primary)] hover:underline"
            onClick={() => {
              setShowAdd(true);
              setEditId(null);
              setForm(emptyAutoLineItemForm());
              setFormErrors({});
            }}
          >
            <Plus size={14} /> Dodaj pozycję automatyczną
          </button>
        )}
      </div>

      <div className="mb-3 flex items-start gap-2 border-l-2 border-sky-400 bg-sky-50 px-3 py-2 text-xs text-sky-900 dark:bg-sky-900/20 dark:text-sky-100">
        <Info size={14} className="mt-0.5 shrink-0" />
        <span>
          Pozycji automatycznej nie wybiera się ręcznie. Dopisuje się sama do nowego zamówienia,
          gdy trafi na nie wskazana pozycja wyzwalająca z tego cennika — z tą samą ilością
          co pozycja wyzwalająca albo z ilością stałą podaną niżej.
        </span>
      </div>

      {showAdd && (
        <AutoLineItemForm
          form={form}
          errors={formErrors}
          upd={upd}
          onTierChange={onTierChange}
          onTierAdd={onTierAdd}
          onTierRemove={onTierRemove}
          triggerOptions={triggerOptions}
          onSave={() => save(form)}
          onCancel={() => {
            setShowAdd(false);
            setFormErrors({});
          }}
          title="Nowa pozycja automatyczna"
        />
      )}

      {autoItems.length === 0 ? (
        <p className="text-sm text-gray-400 dark:text-gray-500">Brak pozycji automatycznych.</p>
      ) : (
        <div className="bg-white dark:bg-gray-800 overflow-hidden">
          {/* Mobile cards */}
          <div className="sm:hidden divide-y divide-gray-100 dark:divide-gray-700">
            {[...activeItems, ...withdrawnItems].map((li) =>
              editId === li.id ? (
                <div key={li.id} className="px-3 py-3">
                  <AutoLineItemForm
                    form={form}
                    errors={formErrors}
                    upd={upd}
                    onTierChange={onTierChange}
                    onTierAdd={onTierAdd}
                    onTierRemove={onTierRemove}
                    triggerOptions={triggerOptions}
                    onSave={() => save(form, li.id)}
                    onCancel={cancelEdit}
                    title="Edytuj pozycję automatyczną"
                  />
                </div>
              ) : (
                <div
                  key={li.id}
                  className={`px-3 py-3 ${li.is_active ? "" : "bg-gray-50/70 dark:bg-gray-900/30"}`}
                >
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex-1 min-w-0">
                      <p
                        className={`text-sm font-medium leading-snug ${li.is_active ? "text-gray-900 dark:text-gray-100" : "text-gray-400 line-through dark:text-gray-500"}`}
                      >
                        {li.name}
                      </p>
                      <WithdrawnBadge li={li} />
                      <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                        {rateLabel(li)}
                      </p>
                    </div>
                    {isDirector && (
                      <div className="flex items-center gap-2 shrink-0">
                        <button
                          className="text-gray-400 dark:text-gray-500 hover:text-[var(--color-primary)]"
                          title="Edytuj"
                          onClick={() => startEdit(li)}
                        >
                          <Pencil size={14} />
                        </button>
                        <LineItemRowActions
                          li={li}
                          busy={busy}
                          onWithdraw={() => withdraw(li)}
                          onRestore={() => restore(li)}
                          onDelete={() => deleteMutation.mutate(li.id)}
                        />
                      </div>
                    )}
                  </div>
                  <div className="mt-2 grid grid-cols-2 gap-x-4 gap-y-1 text-xs">
                    <div>
                      <span className="text-gray-400 dark:text-gray-500 uppercase tracking-wide text-[10px]">Dodawana przy</span>
                      <p className="text-gray-700 dark:text-gray-300 font-medium"><AutoTriggerNames li={li} /></p>
                    </div>
                    <div>
                      <span className="text-gray-400 dark:text-gray-500 uppercase tracking-wide text-[10px]">Ilość</span>
                      <p className="text-gray-700 dark:text-gray-300">{quantityLabel(li)}</p>
                    </div>
                    <div className="col-span-2">
                      <AutoScopeBadges li={li} />
                    </div>
                  </div>
                </div>
              ),
            )}
          </div>

          {/* Desktop table */}
          <table className="hidden sm:table w-full text-xs">
            <thead className="bg-gray-50 dark:bg-gray-800/50 border-b border-gray-200 dark:border-gray-700">
              <tr>
                {["Nazwa", "Jm.", "Stawka / kwota", "Dodawana przy", "Ilość", "Zakres"].map((h, i) => (
                  <th
                    key={h}
                    className={`px-4 py-2 font-mono text-xs uppercase tracking-widest text-gray-500 dark:text-gray-400 ${i === 2 ? "text-right" : "text-left"}`}
                  >
                    {h}
                  </th>
                ))}
                {isDirector && <th className="px-4 py-2" />}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {[...activeItems, ...withdrawnItems].map((li) =>
                editId === li.id ? (
                  <tr key={li.id}>
                    <td colSpan={isDirector ? 7 : 6} className="px-4 py-3">
                      <AutoLineItemForm
                        form={form}
                        errors={formErrors}
                        upd={upd}
                        onTierChange={onTierChange}
                        onTierAdd={onTierAdd}
                        onTierRemove={onTierRemove}
                        triggerOptions={triggerOptions}
                        onSave={() => save(form, li.id)}
                        onCancel={cancelEdit}
                        title="Edytuj pozycję automatyczną"
                      />
                    </td>
                  </tr>
                ) : (
                  <tr
                    key={li.id}
                    className={`hover:bg-gray-50 dark:hover:bg-gray-700/50 ${li.is_active ? "" : "bg-gray-50/70 dark:bg-gray-900/30"}`}
                  >
                    <td className="px-4 py-3 font-medium">
                      <span
                        className={
                          li.is_active
                            ? "text-gray-900 dark:text-gray-100"
                            : "text-gray-400 line-through dark:text-gray-500"
                        }
                      >
                        {li.name}
                      </span>
                      <WithdrawnBadge li={li} />
                    </td>
                    <td className="px-4 py-3 text-gray-500 dark:text-gray-400">{unitLabel(li)}</td>
                    <td className="px-4 py-3 text-right text-gray-700 dark:text-gray-300">
                      {li.auto_pricing_mode === "time_based" ? (
                        <span className="block text-[11px] leading-snug" title={TIME_BASED_TIP}>
                          {(li.auto_time_tiers ?? []).map((t) => (
                            <span key={t.days} className="block whitespace-nowrap">
                              do {t.days} dni: {formatAmount(t.amount)} PLN
                            </span>
                          ))}
                          <span className="block text-[10px] text-gray-400">potem 0 PLN</span>
                        </span>
                      ) : (
                        <>{formatAmount(li.base_rate)} PLN</>
                      )}
                      {li.auto_pricing_mode === "lump_sum" && (
                        <span className="block text-[10px] text-emerald-600 dark:text-emerald-300">
                          pełna kwota
                        </span>
                      )}
                      {li.auto_pricing_mode === "rate" && li.high_priority_rate && (
                        <span className="block text-[10px] text-gray-400 dark:text-gray-500">
                          wys. priorytet: {formatAmount(li.high_priority_rate)} PLN
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-gray-700 dark:text-gray-300">
                      <AutoTriggerNames li={li} />
                    </td>
                    <td className="px-4 py-3 text-gray-500 dark:text-gray-400">{quantityLabel(li)}</td>
                    <td className="px-4 py-3">
                      <AutoScopeBadges li={li} />
                    </td>
                    {isDirector && (
                      <td className="px-4 py-3 text-right">
                        <div className="flex items-center justify-end gap-2">
                          <button
                            className="text-gray-400 dark:text-gray-500 hover:text-[var(--color-primary)]"
                            title="Edytuj"
                            onClick={() => startEdit(li)}
                          >
                            <Pencil size={14} />
                          </button>
                          <LineItemRowActions
                            li={li}
                            busy={busy}
                            onWithdraw={() => withdraw(li)}
                            onRestore={() => restore(li)}
                            onDelete={() => deleteMutation.mutate(li.id)}
                          />
                        </div>
                      </td>
                    )}
                  </tr>
                ),
              )}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}

function AutoLineItemForm({
  form,
  errors,
  upd,
  onTierChange,
  onTierAdd,
  onTierRemove,
  triggerOptions,
  onSave,
  onCancel,
  title,
}: {
  form: AutoLineItemFormData;
  errors: Record<string, string>;
  upd: (k: keyof AutoLineItemFormData, v: string | boolean | string[]) => void;
  onTierChange: (i: number, k: "days" | "amount", v: string) => void;
  onTierAdd: () => void;
  onTierRemove: (i: number) => void;
  triggerOptions: { value: string; label: string }[];
  onSave: () => void;
  onCancel: () => void;
  title: string;
}) {
  const inputCls = (bad?: string) =>
    `block w-full rounded-md border px-3 py-1.5 text-sm ${bad ? "border-red-400" : "border-gray-300"}`;
  return (
    <div className="mb-4 rounded-xl border border-sky-400 bg-sky-50/60 dark:bg-sky-900/10 p-4">
      <p className="text-sm font-semibold text-sky-800 dark:text-sky-200 mb-3">{title}</p>
      <div className="grid grid-cols-2 gap-3 mb-3">
        <div className="col-span-2">
          <label className="text-xs font-medium text-gray-700 dark:text-gray-300 block mb-1">
            Nazwa<span className="text-red-500 ml-0.5">*</span>
          </label>
          <input
            type="text"
            value={form.name}
            onChange={(e) => upd("name", e.target.value)}
            placeholder='np. "Premia za terminowość", "Dojazd na teren"'
            className={inputCls(errors.name)}
          />
          {errors.name && <p className="text-xs text-red-600 mt-0.5">{errors.name}</p>}
        </div>
        {/* Sposób wyceny — trzy tryby (radio), każdy z opisem po najechaniu. */}
        <fieldset className="col-span-2">
          <legend className="text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
            Sposób wyceny
          </legend>
          <div className="flex flex-wrap gap-x-5 gap-y-1.5">
            {PRICING_MODES.map((m) => (
              <label
                key={m.value}
                className="flex items-center gap-2 cursor-pointer select-none"
                title={m.tip}
              >
                <input
                  type="radio"
                  name="auto_pricing_mode"
                  value={m.value}
                  checked={form.auto_pricing_mode === m.value}
                  onChange={() => upd("auto_pricing_mode", m.value)}
                  className="border-gray-300"
                />
                <span className="text-sm text-gray-700 dark:text-gray-300">{m.label}</span>
                <Info size={13} className="text-gray-400" />
              </label>
            ))}
          </div>
        </fieldset>
        {form.auto_pricing_mode === "lump_sum" && (
          <div>
            <label className="text-xs font-medium text-gray-700 dark:text-gray-300 block mb-1">
              Kwota [PLN]<span className="text-red-500 ml-0.5">*</span>
            </label>
            <input
              type="number"
              min="0"
              step="0.01"
              value={form.base_rate}
              onChange={(e) => upd("base_rate", e.target.value)}
              className={inputCls(errors.base_rate)}
            />
            {errors.base_rate && <p className="text-xs text-red-600 mt-0.5">{errors.base_rate}</p>}
          </div>
        )}
        {form.auto_pricing_mode === "time_based" && (
          <div className="col-span-2">
            <label className="text-xs font-medium text-gray-700 dark:text-gray-300 block mb-1">
              Progi: dni od przyjęcia zamówienia → kwota obowiązująca przed przekroczeniem
              <span className="text-red-500 ml-0.5">*</span>
            </label>
            <div className="space-y-1.5">
              {form.auto_time_tiers.map((t, i) => (
                <div key={i} className="flex items-center gap-2">
                  <span className="text-xs text-gray-500 w-6 text-right">{i + 1}.</span>
                  <span className="text-xs text-gray-600 dark:text-gray-300">do</span>
                  <input
                    type="number"
                    min="1"
                    step="1"
                    value={t.days}
                    onChange={(e) => onTierChange(i, "days", e.target.value)}
                    placeholder="dni"
                    className={`${inputCls(errors.auto_time_tiers)} w-24`}
                  />
                  <span className="text-xs text-gray-600 dark:text-gray-300">dni →</span>
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    value={t.amount}
                    onChange={(e) => onTierChange(i, "amount", e.target.value)}
                    placeholder="kwota [PLN]"
                    className={`${inputCls(errors.auto_time_tiers)} w-36`}
                  />
                  <span className="text-xs text-gray-600 dark:text-gray-300">PLN</span>
                  <button
                    type="button"
                    className="text-gray-400 hover:text-red-600 disabled:opacity-30"
                    title="Usuń próg"
                    disabled={form.auto_time_tiers.length === 1}
                    onClick={() => onTierRemove(i)}
                  >
                    <Trash2 size={14} />
                  </button>
                </div>
              ))}
            </div>
            <div className="mt-1.5 flex items-center gap-3">
              <button
                type="button"
                className="inline-flex items-center gap-1 text-xs text-[var(--color-primary)] hover:underline"
                onClick={onTierAdd}
              >
                <Plus size={12} /> Dodaj próg
              </button>
              <span className="text-[11px] text-gray-500 dark:text-gray-400">
                Po przekroczeniu ostatniego progu pozycja wynosi 0 PLN.
              </span>
            </div>
            {errors.auto_time_tiers && (
              <p className="text-xs text-red-600 mt-0.5">{errors.auto_time_tiers}</p>
            )}
          </div>
        )}
        {form.auto_pricing_mode === "rate" && (
          <>
            <div>
              <label className="text-xs font-medium text-gray-700 dark:text-gray-300 block mb-1">
                Jednostka<span className="text-red-500 ml-0.5">*</span>
              </label>
              <input
                type="text"
                value={form.unit}
                onChange={(e) => upd("unit", e.target.value)}
                placeholder='np. "szt.", "komplet"'
                className={inputCls(errors.unit)}
              />
              {errors.unit && <p className="text-xs text-red-600 mt-0.5">{errors.unit}</p>}
            </div>
            <div>
              <label className="text-xs font-medium text-gray-700 dark:text-gray-300 block mb-1">
                Stawka bazowa [PLN]<span className="text-red-500 ml-0.5">*</span>
              </label>
              <input
                type="number"
                min="0"
                step="0.01"
                value={form.base_rate}
                onChange={(e) => upd("base_rate", e.target.value)}
                className={inputCls(errors.base_rate)}
              />
              {errors.base_rate && <p className="text-xs text-red-600 mt-0.5">{errors.base_rate}</p>}
            </div>
            <div>
              <label className="text-xs font-medium text-gray-700 dark:text-gray-300 block mb-1">
                Stawka wysoki priorytet [PLN] (opcjonalnie)
              </label>
              <input
                type="number"
                min="0"
                step="0.01"
                value={form.high_priority_rate}
                onChange={(e) => upd("high_priority_rate", e.target.value)}
                className={inputCls()}
              />
            </div>
            <div>
              <label className="text-xs font-medium text-gray-700 dark:text-gray-300 block mb-1">
                Ilość (puste = jak w pozycji wyzwalającej)
              </label>
              <input
                type="number"
                min="0"
                step="0.01"
                value={form.auto_quantity}
                onChange={(e) => upd("auto_quantity", e.target.value)}
                placeholder="jak w pozycji wyzwalającej"
                className={inputCls(errors.auto_quantity)}
              />
              {errors.auto_quantity && (
                <p className="text-xs text-red-600 mt-0.5">{errors.auto_quantity}</p>
              )}
            </div>
          </>
        )}
        <div className="col-span-2">
          <label className="text-xs font-medium text-gray-700 dark:text-gray-300 block mb-1">
            Dodawaj przy pozycjach realizacyjnych<span className="text-red-500 ml-0.5">*</span>
          </label>
          <p className="text-[11px] text-gray-500 dark:text-gray-400 mb-1">
            Można zaznaczyć kilka — pozycja dopisze się, gdy na zamówieniu znajdzie się
            którakolwiek z nich (zawsze tylko raz).
          </p>
          {triggerOptions.length > 0 && (
            <div
              className={`max-h-48 overflow-y-auto rounded-md border bg-white px-3 py-2 dark:bg-gray-800 ${
                errors.auto_triggers ? "border-red-400" : "border-gray-300 dark:border-gray-600"
              }`}
              data-testid="auto-triggers"
            >
              {triggerOptions.map((o) => {
                const checked = form.auto_triggers.includes(o.value);
                return (
                  <label
                    key={o.value}
                    className="flex items-center gap-2 py-0.5 text-sm cursor-pointer select-none text-gray-800 dark:text-gray-200"
                  >
                    <input
                      type="checkbox"
                      checked={checked}
                      onChange={() =>
                        upd(
                          "auto_triggers",
                          checked
                            ? form.auto_triggers.filter((v) => v !== o.value)
                            : [...form.auto_triggers, o.value],
                        )
                      }
                    />
                    <span>{o.label}</span>
                  </label>
                );
              })}
            </div>
          )}
          {errors.auto_triggers && (
            <p className="text-xs text-red-600 mt-0.5">{errors.auto_triggers}</p>
          )}
          {triggerOptions.length === 0 && (
            <p className="text-xs text-amber-700 mt-0.5">
              Brak aktywnych pozycji realizacyjnych w tej umowie — najpierw dodaj pozycję
              wyzwalającą w sekcji wyżej.
            </p>
          )}
        </div>
      </div>
      <div className="flex flex-wrap gap-x-6 gap-y-2 mt-1 mb-3">
        <label className="flex items-center gap-2 cursor-pointer select-none" title={POWER_ONLY_TIP}>
          <input
            type="checkbox"
            checked={form.auto_power_projects_only}
            onChange={(e) => upd("auto_power_projects_only", e.target.checked)}
            className="rounded border-gray-300"
          />
          <span className="text-sm text-gray-700 dark:text-gray-300">
            Tylko projekty energetyczne (DLC01)
          </span>
          <Info size={13} className="text-gray-400" />
        </label>
        <label className="flex items-center gap-2 cursor-pointer select-none" title={ONCE_PER_PROJECT_TIP}>
          <input
            type="checkbox"
            checked={form.auto_once_per_project}
            onChange={(e) => upd("auto_once_per_project", e.target.checked)}
            className="rounded border-gray-300"
          />
          <span className="text-sm text-gray-700 dark:text-gray-300">
            Jednorazowa dla projektu
          </span>
          <Info size={13} className="text-gray-400" />
        </label>
      </div>
      {errors._ && <p className="text-xs text-red-600 mb-2">{errors._}</p>}
      <div className="flex gap-2">
        <Button size="sm" onClick={onSave}>
          Zapisz
        </Button>
        <Button size="sm" variant="secondary" onClick={onCancel}>
          Anuluj
        </Button>
      </div>
    </div>
  );
}

// ── Penalty Terms section ──────────────────────────────────────────────────────

interface PenaltyTermForm {
  line_item_type: string;
  penalty_per_day_pct: string;
  penalty_per_day_amount: string;
  flat_penalty_amount: string;
  max_penalty_pct: string;
  max_penalty_amount: string;
  termination_after_days: string;
  termination_allowed: boolean;
  notes: string;
}
const emptyPenaltyTermForm = (): PenaltyTermForm => ({
  line_item_type: "",
  penalty_per_day_pct: "",
  penalty_per_day_amount: "",
  flat_penalty_amount: "",
  max_penalty_pct: "",
  max_penalty_amount: "",
  termination_after_days: "",
  termination_allowed: false,
  notes: "",
});

function PenaltyTermsSection({
  agreementId,
  terms,
  lineItems,
  isDirector,
}: {
  agreementId: string;
  terms: PenaltyTerm[];
  lineItems: LineItemType[];
  isDirector: boolean;
}) {
  const qc = useQueryClient();
  const [showAdd, setShowAdd] = useState(false);
  const [editId, setEditId] = useState<string | null>(null);
  const [form, setForm] = useState<PenaltyTermForm>(emptyPenaltyTermForm());
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});

  const saveMutation = useMutation({
    mutationFn: (payload: { data: PenaltyTermForm; id?: string }) => {
      const body = {
        agreement: agreementId,
        line_item_type: payload.data.line_item_type || null,
        penalty_per_day_pct: payload.data.penalty_per_day_pct || null,
        penalty_per_day_amount: payload.data.penalty_per_day_amount || null,
        flat_penalty_amount: payload.data.flat_penalty_amount || null,
        max_penalty_pct: payload.data.max_penalty_pct || null,
        max_penalty_amount: payload.data.max_penalty_amount || null,
        termination_after_days: payload.data.termination_after_days
          ? parseInt(payload.data.termination_after_days)
          : null,
        termination_allowed: payload.data.termination_allowed,
        notes: payload.data.notes,
      };
      if (payload.id) {
        return apiClient.patch(
          `/agreements/${agreementId}/penalty-terms/${payload.id}/`,
          body,
        );
      }
      return apiClient.post(`/agreements/${agreementId}/penalty-terms/`, body);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["agreement", agreementId] });
      setShowAdd(false);
      setEditId(null);
      setForm(emptyPenaltyTermForm());
      setFormErrors({});
    },
    onError: () => setFormErrors({ _: "Błąd zapisu. Sprawdź dane." }),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) =>
      apiClient.delete(`/agreements/${agreementId}/penalty-terms/${id}/`),
    onSuccess: () =>
      qc.invalidateQueries({ queryKey: ["agreement", agreementId] }),
  });

  const upd = <K extends keyof PenaltyTermForm>(k: K, v: PenaltyTermForm[K]) =>
    setForm((p) => ({ ...p, [k]: v }));

  return (
    <section className="mb-6">
      <div className="flex items-center justify-between mb-3">
        <h2 className="text-base font-semibold text-gray-900 dark:text-gray-100 flex items-center gap-2">
          <AlertTriangle size={16} className="text-amber-500" />
          Kary umowne — warunki ({terms.length})
        </h2>
        {isDirector && (
          <button
            className="inline-flex items-center gap-1 text-sm text-[var(--color-primary)] hover:underline"
            onClick={() => {
              setShowAdd(true);
              setForm(emptyPenaltyTermForm());
              setFormErrors({});
              setEditId(null);
            }}
          >
            <Plus size={14} /> Dodaj karę
          </button>
        )}
      </div>

      {(showAdd || editId) && (
        <PenaltyTermForm
          form={form}
          errors={formErrors}
          lineItems={lineItems}
          upd={upd}
          onSave={() =>
            saveMutation.mutate({ data: form, id: editId ?? undefined })
          }
          onCancel={() => {
            setShowAdd(false);
            setEditId(null);
            setFormErrors({});
          }}
          isPending={saveMutation.isPending}
          title={editId ? "Edytuj warunki kary" : "Nowe warunki kary"}
        />
      )}

      {terms.length === 0 && !showAdd ? (
        <p className="text-sm text-gray-400 dark:text-gray-500">
          Brak zdefiniowanych kar umownych.
        </p>
      ) : (
        <div className="flex flex-col gap-2">
          {terms.map((term) =>
            editId === term.id ? null : (
              <div
                key={term.id}
                className="bg-white dark:bg-gray-800 p-4"
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="flex-1">
                    <p className="font-medium text-gray-900 dark:text-gray-100 mb-1">
                      {term.line_item_type_name
                        ? `Pozycja: ${term.line_item_type_name}`
                        : "Kara ogólna (całe zamówienie)"}
                    </p>
                    <div className="grid grid-cols-2 gap-x-6 gap-y-1 text-sm text-gray-600 dark:text-gray-400">
                      {term.is_delay_penalty ? (
                        <>
                          {term.penalty_per_day_pct && (
                            <span>
                              Stawka/dzień:{" "}
                              <strong>{term.penalty_per_day_pct}%</strong>{" "}
                              wartości pozycji
                            </span>
                          )}
                          {term.penalty_per_day_amount && (
                            <span>
                              Stawka/dzień:{" "}
                              <strong>
                                {formatAmount(term.penalty_per_day_amount)} PLN
                              </strong>
                            </span>
                          )}
                        </>
                      ) : (
                        <span className="col-span-2">
                          Kara jednorazowa:{" "}
                          <strong>
                            {formatAmount(term.flat_penalty_amount ?? "0")} PLN
                          </strong>
                        </span>
                      )}
                      {term.max_penalty_pct && (
                        <span>
                          Maks. kara: <strong>{term.max_penalty_pct}%</strong>{" "}
                          wartości
                        </span>
                      )}
                      {term.max_penalty_amount && (
                        <span>
                          Maks. kara:{" "}
                          <strong>
                            {formatAmount(term.max_penalty_amount)} PLN
                          </strong>
                        </span>
                      )}
                      {term.termination_after_days && (
                        <span>
                          Zerwanie po:{" "}
                          <strong>{term.termination_after_days} dniach</strong>{" "}
                          opóźnienia
                        </span>
                      )}
                    </div>
                    {term.termination_allowed && (
                      <p className="mt-1.5 text-xs font-semibold text-red-600 flex items-center gap-1">
                        <AlertTriangle size={12} /> Możliwość zerwania z winy
                        podwykonawcy
                      </p>
                    )}
                    {term.notes && (
                      <p className="mt-1.5 text-xs text-gray-400 dark:text-gray-500">
                        {term.notes}
                      </p>
                    )}
                  </div>
                  {isDirector && (
                    <div className="flex items-center gap-2 shrink-0">
                      <button
                        className="text-gray-400 dark:text-gray-500 hover:text-[var(--color-primary)]"
                        onClick={() => {
                          setEditId(term.id);
                          setShowAdd(false);
                          setForm({
                            line_item_type: term.line_item_type ?? "",
                            penalty_per_day_pct: term.penalty_per_day_pct ?? "",
                            penalty_per_day_amount:
                              term.penalty_per_day_amount ?? "",
                            flat_penalty_amount: term.flat_penalty_amount ?? "",
                            max_penalty_pct: term.max_penalty_pct ?? "",
                            max_penalty_amount: term.max_penalty_amount ?? "",
                            termination_after_days: term.termination_after_days
                              ? String(term.termination_after_days)
                              : "",
                            termination_allowed: term.termination_allowed,
                            notes: term.notes,
                          });
                          setFormErrors({});
                        }}
                        title="Edytuj"
                      >
                        <Pencil size={14} />
                      </button>
                      <button
                        className="text-gray-400 dark:text-gray-500 hover:text-red-600 disabled:opacity-40"
                        disabled={deleteMutation.isPending}
                        onClick={() => {
                          if (confirm("Usunąć te warunki kary?")) {
                            deleteMutation.mutate(term.id);
                          }
                        }}
                        title="Usuń"
                      >
                        <Trash2 size={14} />
                      </button>
                    </div>
                  )}
                </div>
              </div>
            ),
          )}
          {/* Inline edit form */}
          {editId && (
            <PenaltyTermForm
              form={form}
              errors={formErrors}
              lineItems={lineItems}
              upd={upd}
              onSave={() => saveMutation.mutate({ data: form, id: editId })}
              onCancel={() => {
                setEditId(null);
                setFormErrors({});
              }}
              isPending={saveMutation.isPending}
              title="Edytuj warunki kary"
            />
          )}
        </div>
      )}
    </section>
  );
}

function PenaltyTermForm({
  form,
  errors,
  lineItems,
  upd,
  onSave,
  onCancel,
  isPending,
  title,
}: {
  form: PenaltyTermForm;
  errors: Record<string, string>;
  lineItems: LineItemType[];
  upd: <K extends keyof PenaltyTermForm>(k: K, v: PenaltyTermForm[K]) => void;
  onSave: () => void;
  onCancel: () => void;
  isPending: boolean;
  title: string;
}) {
  return (
    <div className="mb-4 rounded-xl border border-amber-200 bg-amber-50 p-4">
      <p className="text-sm font-semibold text-amber-800 mb-3">{title}</p>
      <div className="grid grid-cols-2 gap-3 mb-3">
        <div className="col-span-2">
          <label className="text-xs font-medium text-gray-700 dark:text-gray-300 block mb-1">
            Pozycja realizacyjna (opcjonalnie — puste = kara na całe zamówienie)
          </label>
          <select
            className="block w-full rounded-md border border-gray-300 px-3 py-1.5 text-sm"
            value={form.line_item_type}
            onChange={(e) => upd("line_item_type", e.target.value)}
          >
            <option value="">— kara ogólna (całe zamówienie) —</option>
            {lineItems
              .filter((li) => li.is_active)
              .map((li) => (
                <option key={li.id} value={li.id}>
                  {li.name} ({li.unit})
                </option>
              ))}
          </select>
        </div>
        {/* Delay penalty fields */}
        <div className="col-span-2">
          <p className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-2">
            Kara za opóźnienie (naliczana dziennie)
          </p>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-medium text-gray-700 dark:text-gray-300 block mb-1">
                Stawka/dzień [% wartości]
              </label>
              <input
                type="number"
                min="0"
                step="0.0001"
                value={form.penalty_per_day_pct}
                onChange={(e) => upd("penalty_per_day_pct", e.target.value)}
                placeholder="np. 0.5"
                className="block w-full rounded-md border border-gray-300 px-3 py-1.5 text-sm"
              />
            </div>
            <div>
              <label className="text-xs font-medium text-gray-700 dark:text-gray-300 block mb-1">
                Stawka/dzień [PLN]
              </label>
              <input
                type="number"
                min="0"
                step="0.01"
                value={form.penalty_per_day_amount}
                onChange={(e) => upd("penalty_per_day_amount", e.target.value)}
                placeholder="np. 500.00"
                className="block w-full rounded-md border border-gray-300 px-3 py-1.5 text-sm"
              />
            </div>
          </div>
        </div>

        {/* One-time penalty field */}
        <div className="col-span-2">
          <div className="flex items-center gap-2 my-1">
            <hr className="flex-1 border-gray-200 dark:border-gray-700" />
            <span className="text-xs text-gray-400 dark:text-gray-500 font-medium">
              LUB — kara jednorazowa
            </span>
            <hr className="flex-1 border-gray-200 dark:border-gray-700" />
          </div>
          <label className="text-xs font-medium text-gray-700 dark:text-gray-300 block mb-1">
            Kwota kary jednorazowej [PLN]
          </label>
          <input
            type="number"
            min="0"
            step="0.01"
            value={form.flat_penalty_amount}
            onChange={(e) => upd("flat_penalty_amount", e.target.value)}
            placeholder="np. 5000.00"
            className="block w-full rounded-md border border-gray-300 px-3 py-1.5 text-sm"
          />
          <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">
            Dla kar jednorazowych (np. za niewykonanie umowy). Dyrektor decyduje
            tak/nie przy każdym opóźnieniu.
          </p>
        </div>

        {/* Cap fields */}
        <div className="col-span-2">
          <p className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-2 mt-1">
            Limity kary (opcjonalnie, dotyczy kar dziennych)
          </p>
        </div>
        <div>
          <label className="text-xs font-medium text-gray-700 dark:text-gray-300 block mb-1">
            Maksymalna kara [% wartości]
          </label>
          <input
            type="number"
            min="0"
            step="0.0001"
            value={form.max_penalty_pct}
            onChange={(e) => upd("max_penalty_pct", e.target.value)}
            placeholder="np. 10"
            className="block w-full rounded-md border border-gray-300 px-3 py-1.5 text-sm"
          />
        </div>
        <div>
          <label className="text-xs font-medium text-gray-700 dark:text-gray-300 block mb-1">
            Maksymalna kara [PLN]
          </label>
          <input
            type="number"
            min="0"
            step="0.01"
            value={form.max_penalty_amount}
            onChange={(e) => upd("max_penalty_amount", e.target.value)}
            placeholder="np. 20000.00"
            className="block w-full rounded-md border border-gray-300 px-3 py-1.5 text-sm"
          />
        </div>
        <div>
          <label className="text-xs font-medium text-gray-700 dark:text-gray-300 block mb-1">
            Zerwanie dopuszczalne po ilu dniach opóźnienia
          </label>
          <input
            type="number"
            min="0"
            step="1"
            value={form.termination_after_days}
            onChange={(e) => upd("termination_after_days", e.target.value)}
            placeholder="np. 30"
            className="block w-full rounded-md border border-gray-300 px-3 py-1.5 text-sm"
          />
        </div>
        <div className="flex items-end pb-1">
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={form.termination_allowed}
              onChange={(e) => upd("termination_allowed", e.target.checked)}
              className="rounded"
            />
            <span className="text-sm text-gray-700 dark:text-gray-300">
              Możliwość zerwania z winy podwykonawcy
            </span>
          </label>
        </div>
        <div className="col-span-2">
          <label className="text-xs font-medium text-gray-700 dark:text-gray-300 block mb-1">
            Uwagi
          </label>
          <textarea
            rows={2}
            value={form.notes}
            onChange={(e) => upd("notes", e.target.value)}
            className="block w-full rounded-md border border-gray-300 px-3 py-1.5 text-sm"
          />
        </div>
      </div>
      {errors._ && <p className="text-xs text-red-600 mb-2">{errors._}</p>}
      <div className="flex gap-2">
        <Button size="sm" loading={isPending} onClick={onSave}>
          Zapisz
        </Button>
        <Button size="sm" variant="secondary" onClick={onCancel}>
          Anuluj
        </Button>
      </div>
    </div>
  );
}

// ── Penalty Records section ────────────────────────────────────────────────────

interface PenaltyRecordForm {
  order: string;
  penalty_term: string;
  delay_days: string;
  amount: string;
  is_termination: boolean;
  note: string;
}
const emptyRecordForm = (): PenaltyRecordForm => ({
  order: "",
  penalty_term: "",
  delay_days: "0",
  amount: "",
  is_termination: false,
  note: "",
});

function PenaltyRecordsSection({
  agreementId,
  records,
  terms,
  orders,
  isDirector,
}: {
  agreementId: string;
  records: PenaltyRecord[];
  terms: PenaltyTerm[];
  orders: Order[];
  isDirector: boolean;
}) {
  const qc = useQueryClient();
  const [showAdd, setShowAdd] = useState(false);
  const [form, setForm] = useState<PenaltyRecordForm>(emptyRecordForm());
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});

  const createMutation = useMutation({
    mutationFn: (payload: PenaltyRecordForm) =>
      apiClient.post(`/agreements/${agreementId}/penalty-records/`, {
        agreement: agreementId,
        order: payload.order || null,
        penalty_term: payload.penalty_term || null,
        delay_days: parseInt(payload.delay_days) || 0,
        amount: payload.amount,
        is_termination: payload.is_termination,
        note: payload.note,
      }),
    onSuccess: () => {
      qc.invalidateQueries({
        queryKey: ["agreement-penalty-records", agreementId],
      });
      qc.invalidateQueries({ queryKey: ["agreement-summary", agreementId] });
      setShowAdd(false);
      setForm(emptyRecordForm());
      setFormErrors({});
    },
    onError: () => setFormErrors({ _: "Błąd zapisu. Sprawdź dane." }),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) =>
      apiClient.delete(`/agreements/${agreementId}/penalty-records/${id}/`),
    onSuccess: () => {
      qc.invalidateQueries({
        queryKey: ["agreement-penalty-records", agreementId],
      });
      qc.invalidateQueries({ queryKey: ["agreement-summary", agreementId] });
    },
  });

  const validate = (): boolean => {
    const errs: Record<string, string> = {};
    if (!form.amount || isNaN(parseFloat(form.amount)))
      errs.amount = "Kwota jest wymagana";
    setFormErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const upd = <K extends keyof PenaltyRecordForm>(
    k: K,
    v: PenaltyRecordForm[K],
  ) => setForm((p) => ({ ...p, [k]: v }));

  const totalPenalties = records.reduce(
    (sum, r) => sum + parseFloat(r.amount),
    0,
  );

  return (
    <section className="mb-6">
      <div className="flex items-center justify-between mb-3">
        <h2 className="text-base font-semibold text-gray-900 dark:text-gray-100 flex items-center gap-2">
          <AlertTriangle size={16} className="text-red-500" />
          Historia naliczonych kar
          {records.length > 0 && (
            <span className="text-sm font-normal text-red-600 ml-1">
              (łącznie: {formatAmount(totalPenalties)} PLN)
            </span>
          )}
        </h2>
        {isDirector && (
          <button
            className="inline-flex items-center gap-1 text-sm text-[var(--color-primary)] hover:underline"
            onClick={() => {
              setShowAdd(true);
              setForm(emptyRecordForm());
              setFormErrors({});
            }}
          >
            <Plus size={14} /> Nalicz karę
          </button>
        )}
      </div>

      {showAdd && (
        <div className="mb-4 rounded-xl border border-red-200 bg-red-50 p-4">
          <p className="text-sm font-semibold text-red-800 mb-3">
            Nalicz karę umowną
          </p>
          <div className="grid grid-cols-2 gap-3 mb-3">
            <div>
              <label className="text-xs font-medium text-gray-700 dark:text-gray-300 block mb-1">
                Zamówienie (opcjonalnie)
              </label>
              <select
                className="block w-full rounded-md border border-gray-300 px-3 py-1.5 text-sm"
                value={form.order}
                onChange={(e) => upd("order", e.target.value)}
              >
                <option value="">— brak —</option>
                {orders.map((o) => (
                  <option key={o.id} value={o.id}>
                    {o.order_number}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="text-xs font-medium text-gray-700 dark:text-gray-300 block mb-1">
                Podstawa kary (opcjonalnie)
              </label>
              <select
                className="block w-full rounded-md border border-gray-300 px-3 py-1.5 text-sm"
                value={form.penalty_term}
                onChange={(e) => upd("penalty_term", e.target.value)}
              >
                <option value="">— brak —</option>
                {terms.map((t) => (
                  <option key={t.id} value={t.id}>
                    {t.line_item_type_name ?? "Ogólna (całe zamówienie)"}
                    {t.penalty_per_day_pct
                      ? ` — ${t.penalty_per_day_pct}%/dzień`
                      : ""}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="text-xs font-medium text-gray-700 dark:text-gray-300 block mb-1">
                Dni opóźnienia
              </label>
              <input
                type="number"
                min="0"
                value={form.delay_days}
                onChange={(e) => upd("delay_days", e.target.value)}
                className="block w-full rounded-md border border-gray-300 px-3 py-1.5 text-sm"
              />
            </div>
            <div>
              <label className="text-xs font-medium text-gray-700 dark:text-gray-300 block mb-1">
                Kwota kary [PLN]<span className="text-red-500 ml-0.5">*</span>
              </label>
              <input
                type="number"
                min="0"
                step="0.01"
                value={form.amount}
                onChange={(e) => upd("amount", e.target.value)}
                className={`block w-full rounded-md border px-3 py-1.5 text-sm ${formErrors.amount ? "border-red-400" : "border-gray-300"}`}
              />
              {formErrors.amount && (
                <p className="text-xs text-red-600 mt-0.5">
                  {formErrors.amount}
                </p>
              )}
            </div>
            <div className="col-span-2">
              <label className="flex items-center gap-2 cursor-pointer mb-2">
                <input
                  type="checkbox"
                  checked={form.is_termination}
                  onChange={(e) => upd("is_termination", e.target.checked)}
                  className="rounded"
                />
                <span className="text-sm text-red-700 font-medium">
                  Zerwanie umowy z winy podwykonawcy
                </span>
              </label>
            </div>
            <div className="col-span-2">
              <label className="text-xs font-medium text-gray-700 dark:text-gray-300 block mb-1">
                Uzasadnienie
              </label>
              <textarea
                rows={2}
                value={form.note}
                onChange={(e) => upd("note", e.target.value)}
                className="block w-full rounded-md border border-gray-300 px-3 py-1.5 text-sm"
                placeholder="Podstawa naliczenia kary..."
              />
            </div>
          </div>
          {formErrors._ && (
            <p className="text-xs text-red-600 mb-2">{formErrors._}</p>
          )}
          <div className="flex gap-2">
            <Button
              size="sm"
              loading={createMutation.isPending}
              onClick={() => {
                if (validate()) createMutation.mutate(form);
              }}
            >
              Nalicz karę
            </Button>
            <Button
              size="sm"
              variant="secondary"
              onClick={() => setShowAdd(false)}
            >
              Anuluj
            </Button>
          </div>
        </div>
      )}

      {records.length === 0 ? (
        <p className="text-sm text-gray-400 dark:text-gray-500">
          Brak naliczonych kar.
        </p>
      ) : (
        <div className="bg-white dark:bg-gray-800 overflow-hidden">
          <table className="w-full text-xs">
            <thead className="bg-gray-50 dark:bg-gray-800/50 border-b border-gray-200 dark:border-gray-700">
              <tr>
                <th className="text-left px-4 py-2 font-mono text-xs uppercase tracking-widest text-gray-500 dark:text-gray-400">
                  Data
                </th>
                <th className="text-left px-4 py-2 font-mono text-xs uppercase tracking-widest text-gray-500 dark:text-gray-400">
                  Zamówienie
                </th>
                <th className="text-left px-4 py-2 font-mono text-xs uppercase tracking-widest text-gray-500 dark:text-gray-400">
                  Podstawa
                </th>
                <th className="text-right px-4 py-2 font-mono text-xs uppercase tracking-widest text-gray-500 dark:text-gray-400">
                  Dni
                </th>
                <th className="text-right px-4 py-2 font-mono text-xs uppercase tracking-widest text-gray-500 dark:text-gray-400">
                  Kwota
                </th>
                <th className="text-left px-4 py-2 font-mono text-xs uppercase tracking-widest text-gray-500 dark:text-gray-400">
                  Uzasadnienie
                </th>
                <th className="text-left px-4 py-2 font-mono text-xs uppercase tracking-widest text-gray-500 dark:text-gray-400">
                  Naliczył
                </th>
                {isDirector && <th className="px-4 py-2 font-mono text-xs uppercase tracking-widest text-gray-500 dark:text-gray-400" />}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {records.map((r) => (
                <tr
                  key={r.id}
                  className={`hover:bg-gray-50 dark:hover:bg-gray-700/50 ${r.is_termination ? "bg-red-50" : ""}`}
                >
                  <td className="px-4 py-3 text-gray-500 dark:text-gray-400 whitespace-nowrap">
                    {new Date(r.created_at).toLocaleDateString("pl")}
                  </td>
                  <td className="px-4 py-3">
                    {r.order ? (
                      <Link
                        to={`/orders/${r.order}`}
                        className="text-[var(--color-primary)] hover:underline inline-flex items-center gap-1"
                      >
                        {r.order_number} <ExternalLink size={10} />
                      </Link>
                    ) : (
                      <span className="text-gray-400 dark:text-gray-500">
                        —
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-gray-600 dark:text-gray-400">
                    {r.penalty_term_description ?? "—"}
                  </td>
                  <td className="px-4 py-3 text-right text-gray-700 dark:text-gray-300">
                    {r.delay_days}
                  </td>
                  <td className="px-4 py-3 text-right font-semibold text-red-700">
                    {formatAmount(r.amount)} PLN
                  </td>
                  <td className="px-4 py-3 text-gray-600 dark:text-gray-400 max-w-[200px] truncate">
                    {r.is_termination && (
                      <span className="text-red-600 font-semibold mr-1">
                        [Zerwanie]
                      </span>
                    )}
                    {r.note || "—"}
                  </td>
                  <td className="px-4 py-3 text-gray-500 dark:text-gray-400">
                    {r.applied_by_name ?? "—"}
                  </td>
                  {isDirector && (
                    <td className="px-4 py-3 text-right">
                      <button
                        className="text-gray-400 dark:text-gray-500 hover:text-red-600 disabled:opacity-40"
                        disabled={deleteMutation.isPending}
                        onClick={() => {
                          if (confirm("Usunąć tę karę z historii?")) {
                            deleteMutation.mutate(r.id);
                          }
                        }}
                        title="Usuń"
                      >
                        <Trash2 size={14} />
                      </button>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}

// ── Orders + protocols + invoices section ─────────────────────────────────────

function OrderHistorySection({ agreementId }: { agreementId: string }) {
  const [expandedOrder, setExpandedOrder] = useState<string | null>(null);

  const { data: orders = [] } = useQuery({
    queryKey: ["orders", { agreement: agreementId }],
    queryFn: () =>
      apiClient
        .get<{
          data: Order[];
        }>("/orders/", { params: { agreement: agreementId } })
        .then((r) => r.data.data),
  });

  const { data: protocols = [] } = useQuery({
    queryKey: ["protocols", { agreement: agreementId }],
    queryFn: () =>
      apiClient
        .get<{
          data: Protocol[];
        }>("/protocols/", { params: { agreement: agreementId } })
        .then((r) => r.data.data),
  });

  const { data: invoices = [] } = useQuery({
    queryKey: ["invoices", { agreement: agreementId }],
    queryFn: () =>
      apiClient
        .get<{
          data: Invoice[];
        }>("/invoices/", { params: { agreement: agreementId } })
        .then((r) => r.data.data),
  });

  return (
    <section className="mb-6">
      <h2 className="text-base font-semibold text-gray-900 dark:text-gray-100 mb-3">
        Historia zamówień ({orders.length})
      </h2>
      {orders.length === 0 ? (
        <p className="text-sm text-gray-400 dark:text-gray-500">
          Brak zamówień powiązanych z tą umową.
        </p>
      ) : (
        <div className="bg-white dark:bg-gray-800 overflow-hidden">
          <table className="w-full text-xs">
            <thead className="bg-gray-50 dark:bg-gray-800/50 border-b border-gray-200 dark:border-gray-700">
              <tr>
                <th className="w-8 px-4 py-2 font-mono text-xs uppercase tracking-widest text-gray-500 dark:text-gray-400" />
                <th className="text-left px-4 py-2 font-mono text-xs uppercase tracking-widest text-gray-500 dark:text-gray-400">
                  Nr zamówienia
                </th>
                <th className="text-left px-4 py-2 font-mono text-xs uppercase tracking-widest text-gray-500 dark:text-gray-400">
                  Przedsięwzięcie
                </th>
                <th className="text-left px-4 py-2 font-mono text-xs uppercase tracking-widest text-gray-500 dark:text-gray-400">
                  Status
                </th>
                <th className="text-left px-4 py-2 font-mono text-xs uppercase tracking-widest text-gray-500 dark:text-gray-400">
                  Termin
                </th>
              </tr>
            </thead>
            <tbody>
              {orders.map((o) => {
                const orderProtocols = protocols.filter(
                  (p) => p.order_number === o.order_number,
                );
                const orderInvoices = invoices.filter(
                  (i) => i.order_number === o.order_number,
                );
                const isExpanded = expandedOrder === o.id;
                return (
                  <>
                    <tr
                      key={o.id}
                      className="hover:bg-gray-50 dark:hover:bg-gray-700/50 border-b border-gray-100 cursor-pointer"
                      onClick={() => setExpandedOrder(isExpanded ? null : o.id)}
                    >
                      <td className="px-4 py-3 text-gray-400 dark:text-gray-500">
                        {isExpanded ? (
                          <ChevronDown size={14} />
                        ) : (
                          <ChevronRight size={14} />
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <Link
                          to={`/orders/${o.id}`}
                          onClick={(e) => e.stopPropagation()}
                          className="font-medium text-[var(--color-primary)] hover:underline inline-flex items-center gap-1"
                        >
                          {o.order_number}{" "}
                          <ExternalLink
                            size={11}
                            className="text-gray-400 dark:text-gray-500"
                          />
                        </Link>
                      </td>
                      <td className="px-4 py-3 text-gray-600 dark:text-gray-400">
                        {o.project_name ?? "—"}
                      </td>
                      <td className="px-4 py-3">
                        <Badge color={ORDER_STATUS_COLOR[o.status] ?? "gray"}>
                          {ORDER_STATUS_LABEL[o.status] ?? o.status}
                        </Badge>
                      </td>
                      <td className="px-4 py-3 text-gray-500 dark:text-gray-400">
                        {o.deadline
                          ? new Date(o.deadline).toLocaleDateString("pl")
                          : "—"}
                      </td>
                    </tr>
                    {isExpanded && (
                      <tr
                        key={`${o.id}-detail`}
                        className="border-b border-gray-100"
                      >
                        <td
                          colSpan={5}
                          className="px-6 py-3 bg-gray-50 dark:bg-gray-800/50"
                        >
                          {/* Protocols */}
                          {orderProtocols.length > 0 && (
                            <div className="mb-3">
                              <p className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase mb-1.5">
                                Protokoły POCK
                              </p>
                              <div className="flex flex-col gap-1">
                                {orderProtocols.map((p) => (
                                  <div
                                    key={p.id}
                                    className="flex items-center gap-3 text-sm"
                                  >
                                    <Link
                                      to={`/protocols/${p.id}`}
                                      className={`text-[var(--color-primary)] hover:underline inline-flex items-center gap-1 ${p.is_withdrawn ? "line-through text-gray-400 dark:text-gray-500" : ""}`}
                                    >
                                      {p.protocol_number}{" "}
                                      <ExternalLink size={10} />
                                    </Link>
                                    {p.is_withdrawn ? (
                                      <Badge color="gray">Wycofany</Badge>
                                    ) : (
                                      <Badge
                                        color={p.is_final ? "purple" : "blue"}
                                      >
                                        {p.is_final ? "Końcowy" : "Częściowy"}
                                      </Badge>
                                    )}
                                    <Badge
                                      color={
                                        p.accepted_by_contractor === null
                                          ? "gray"
                                          : p.accepted_by_contractor
                                            ? "green"
                                            : "red"
                                      }
                                    >
                                      {p.accepted_by_contractor === null
                                        ? "Oczekuje"
                                        : p.accepted_by_contractor
                                          ? "Zaakceptowany"
                                          : "Odrzucony"}
                                    </Badge>
                                    <span className="text-gray-400 dark:text-gray-500 text-xs">
                                      {new Date(
                                        p.created_at,
                                      ).toLocaleDateString("pl")}
                                    </span>
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}
                          {/* Invoices */}
                          {orderInvoices.length > 0 && (
                            <div>
                              <p className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase mb-1.5">
                                Faktury P-GK
                              </p>
                              <div className="flex flex-col gap-1">
                                {orderInvoices.map((inv) => (
                                  <div
                                    key={inv.id}
                                    className="flex items-center gap-3 text-sm"
                                  >
                                    <Link
                                      to={`/invoices/${inv.id}`}
                                      className="text-[var(--color-primary)] hover:underline inline-flex items-center gap-1"
                                    >
                                      {inv.invoice_number}{" "}
                                      <ExternalLink size={10} />
                                    </Link>
                                    <span className="text-gray-700 dark:text-gray-300 font-medium">
                                      {formatAmount(inv.amount_net)} PLN netto
                                    </span>
                                    <Badge
                                      color={
                                        INVOICE_STATUS_COLOR[inv.status] ??
                                        "gray"
                                      }
                                    >
                                      {INVOICE_STATUS_LABEL[inv.status] ??
                                        inv.status}
                                    </Badge>
                                    <Badge
                                      color={
                                        PAYMENT_STATUS_COLOR[
                                          inv.payment_status
                                        ] ?? "gray"
                                      }
                                    >
                                      {PAYMENT_STATUS_LABEL[
                                        inv.payment_status
                                      ] ?? inv.payment_status}
                                    </Badge>
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}
                          {orderProtocols.length === 0 &&
                            orderInvoices.length === 0 && (
                              <p className="text-xs text-gray-400 dark:text-gray-500">
                                Brak protokołów i faktur dla tego zamówienia.
                              </p>
                            )}
                        </td>
                      </tr>
                    )}
                  </>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}

// ── Invoice history section ────────────────────────────────────────────────────

function InvoiceHistorySection({ agreementId }: { agreementId: string }) {
  const { data: invoices = [], isLoading } = useQuery({
    queryKey: ["invoices", { agreement: agreementId }],
    queryFn: () =>
      apiClient
        .get<{
          data: Invoice[];
        }>("/invoices/", { params: { agreement: agreementId } })
        .then((r) => r.data.data),
  });

  const totalNet = invoices
    .filter((i) => i.status !== "withdrawn")
    .reduce((sum, i) => sum + parseFloat(i.amount_net), 0);
  const totalGross = invoices
    .filter((i) => i.status !== "withdrawn")
    .reduce((sum, i) => sum + parseFloat(i.amount_gross), 0);
  const totalPaid = invoices
    .filter((i) => i.status !== "withdrawn")
    .reduce((sum, i) => sum + parseFloat(i.paid_amount || "0"), 0);

  return (
    <section className="mb-6">
      <div className="flex items-center justify-between mb-3">
        <h2 className="text-base font-semibold text-gray-900 dark:text-gray-100">
          Faktury P-GK (
          {invoices.filter((i) => i.status !== "withdrawn").length})
        </h2>
        {invoices.filter((i) => i.status !== "withdrawn").length > 0 && (
          <div className="flex items-center gap-4 text-sm text-gray-600 dark:text-gray-400">
            <span>
              Netto:{" "}
              <strong className="text-gray-900 dark:text-gray-100">
                {formatAmount(totalNet)} PLN
              </strong>
            </span>
            <span>
              Brutto:{" "}
              <strong className="text-gray-900 dark:text-gray-100">
                {formatAmount(totalGross)} PLN
              </strong>
            </span>
            <span>
              Zapłacono:{" "}
              <strong className="text-green-700">
                {formatAmount(totalPaid)} PLN
              </strong>
            </span>
          </div>
        )}
      </div>
      {isLoading ? (
        <p className="text-sm text-gray-400 dark:text-gray-500">Ładowanie...</p>
      ) : invoices.length === 0 ? (
        <p className="text-sm text-gray-400 dark:text-gray-500">
          Brak faktur powiązanych z tą umową.
        </p>
      ) : (
        <div className="bg-white dark:bg-gray-800 overflow-hidden">
          <table className="w-full text-xs">
            <thead className="bg-gray-50 dark:bg-gray-800/50 border-b border-gray-200 dark:border-gray-700">
              <tr>
                <th className="text-left px-4 py-2 font-mono text-xs uppercase tracking-widest text-gray-500 dark:text-gray-400">
                  Nr faktury
                </th>
                <th className="text-left px-4 py-2 font-mono text-xs uppercase tracking-widest text-gray-500 dark:text-gray-400">
                  Zamówienie
                </th>
                <th className="text-right px-4 py-2 font-mono text-xs uppercase tracking-widest text-gray-500 dark:text-gray-400">
                  Netto
                </th>
                <th className="text-right px-4 py-2 font-mono text-xs uppercase tracking-widest text-gray-500 dark:text-gray-400">
                  Brutto
                </th>
                <th className="text-right px-4 py-2 font-mono text-xs uppercase tracking-widest text-gray-500 dark:text-gray-400">
                  Zapłacono
                </th>
                <th className="text-left px-4 py-2 font-mono text-xs uppercase tracking-widest text-gray-500 dark:text-gray-400">
                  Status
                </th>
                <th className="text-left px-4 py-2 font-mono text-xs uppercase tracking-widest text-gray-500 dark:text-gray-400">
                  Płatność
                </th>
                <th className="text-left px-4 py-2 font-mono text-xs uppercase tracking-widest text-gray-500 dark:text-gray-400">
                  Data
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {invoices.map((inv) => {
                const isWithdrawn = inv.status === "withdrawn";
                return (
                  <tr
                    key={inv.id}
                    className={`hover:bg-gray-50 dark:hover:bg-gray-700/50 ${isWithdrawn ? "opacity-50" : ""}`}
                  >
                    <td className="px-4 py-3">
                      <Link
                        to={`/invoices/${inv.id}`}
                        className={`font-medium text-[var(--color-primary)] hover:underline inline-flex items-center gap-1 ${isWithdrawn ? "line-through" : ""}`}
                      >
                        {inv.invoice_number}{" "}
                        <ExternalLink
                          size={11}
                          className="text-gray-400 dark:text-gray-500"
                        />
                      </Link>
                    </td>
                    <td className="px-4 py-3 text-gray-600 dark:text-gray-400">
                      {inv.order_number ?? "—"}
                    </td>
                    <td className="px-4 py-3 text-right text-gray-700 dark:text-gray-300">
                      {formatAmount(inv.amount_net)} PLN
                    </td>
                    <td className="px-4 py-3 text-right font-medium text-gray-900 dark:text-gray-100">
                      {formatAmount(inv.amount_gross)} PLN
                    </td>
                    <td className="px-4 py-3 text-right text-green-700">
                      {parseFloat(inv.paid_amount || "0") > 0
                        ? `${formatAmount(inv.paid_amount)} PLN`
                        : "—"}
                    </td>
                    <td className="px-4 py-3">
                      <Badge color={INVOICE_STATUS_COLOR[inv.status] ?? "gray"}>
                        {INVOICE_STATUS_LABEL[inv.status] ?? inv.status}
                      </Badge>
                    </td>
                    <td className="px-4 py-3">
                      <Badge
                        color={
                          PAYMENT_STATUS_COLOR[inv.payment_status] ?? "gray"
                        }
                      >
                        {PAYMENT_STATUS_LABEL[inv.payment_status] ??
                          inv.payment_status}
                      </Badge>
                    </td>
                    <td className="px-4 py-3 text-gray-500 dark:text-gray-400">
                      {new Date(inv.submitted_at).toLocaleDateString("pl")}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}

// ── Penalties tab (terms + records together) ──────────────────────────────────

function PenaltiesTab({
  agreementId,
  agreement,
  penaltyRecords,
  isDirector,
}: {
  agreementId: string;
  agreement: Agreement;
  penaltyRecords: PenaltyRecord[];
  isDirector: boolean;
}) {
  const { data: orders = [] } = useQuery({
    queryKey: ["orders", { agreement: agreementId }],
    queryFn: () =>
      apiClient
        .get<{
          data: Order[];
        }>("/orders/", { params: { agreement: agreementId } })
        .then((r) => r.data.data),
  });

  return (
    <>
      <PenaltyTermsSection
        agreementId={agreementId}
        terms={agreement.penalty_terms}
        lineItems={agreement.line_item_types}
        isDirector={isDirector}
      />
      <PenaltyRecordsSection
        agreementId={agreementId}
        records={penaltyRecords}
        terms={agreement.penalty_terms}
        orders={orders}
        isDirector={isDirector}
      />
    </>
  );
}

// ── Edit agreement modal ───────────────────────────────────────────────────────

interface EditAgreementForm {
  title: string;
  signed_date: string;
  valid_until: string;
  is_confidential: boolean;
  auto_accept: boolean;
  payment_term_days: string;
  acceptance_deadline_days: string;
  has_min_order_amount: boolean;
  min_order_amount: string;
  allow_partial_pock: boolean;
  allow_external_costs: boolean;
  allow_official_fee_requests: boolean;
  requires_insurance: boolean;
  min_insurance_sum: string;
  requires_weekly_reports: boolean;
  report_template: "standard" | "complex_lot";
  report_day_of_week: string;
  report_due_time: string;
}

function EditAgreementModal({
  agreement,
  open,
  onClose,
  canManageDoc,
}: {
  agreement: Agreement;
  open: boolean;
  onClose: () => void;
  canManageDoc: boolean;
}) {
  const qc = useQueryClient();
  const [docFile, setDocFile] = useState<File | null>(null);
  const [pricingFile, setPricingFile] = useState<File | null>(null);

  const [form, setForm] = useState<EditAgreementForm>({
    title: agreement.title,
    signed_date: agreement.signed_date,
    valid_until: agreement.valid_until ?? "",
    is_confidential: agreement.is_confidential,
    auto_accept: agreement.auto_accept,
    payment_term_days:
      agreement.payment_term_days != null
        ? String(agreement.payment_term_days)
        : "",
    acceptance_deadline_days:
      agreement.acceptance_deadline_days != null
        ? String(agreement.acceptance_deadline_days)
        : "",
    has_min_order_amount: agreement.min_order_amount != null,
    min_order_amount: agreement.min_order_amount ?? "",
    allow_partial_pock: agreement.allow_partial_pock,
    allow_external_costs: agreement.allow_external_costs ?? true,
    allow_official_fee_requests: agreement.allow_official_fee_requests ?? false,
    requires_insurance: agreement.requires_insurance,
    min_insurance_sum: agreement.min_insurance_sum ?? "",
    requires_weekly_reports: agreement.requires_weekly_reports ?? false,
    report_template: agreement.report_template ?? "standard",
    report_day_of_week:
      agreement.report_day_of_week != null
        ? String(agreement.report_day_of_week)
        : "",
    report_due_time: agreement.report_due_time ?? "",
  });
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});

  const saveMutation = useMutation({
    mutationFn: () => {
      const fd = new FormData();
      fd.append("title", form.title);
      fd.append("signed_date", form.signed_date);
      if (form.valid_until) fd.append("valid_until", form.valid_until);
      fd.append("is_confidential", String(form.is_confidential));
      fd.append("auto_accept", String(form.auto_accept));
      if (form.payment_term_days) fd.append("payment_term_days", form.payment_term_days);
      // Pusty napis = termin domyślny (backend mapuje "" na null), więc wysyłamy zawsze.
      fd.append("acceptance_deadline_days", form.acceptance_deadline_days.trim());
      // Pusty napis czyści minimum po odznaczeniu checkboxa (backend mapuje "" na null).
      fd.append("min_order_amount", form.has_min_order_amount ? form.min_order_amount : "");
      fd.append("allow_partial_pock", String(form.allow_partial_pock));
      fd.append("allow_external_costs", String(form.allow_external_costs));
      fd.append("allow_official_fee_requests", String(form.allow_official_fee_requests));
      fd.append("requires_insurance", String(form.requires_insurance));
      if (form.requires_insurance && form.min_insurance_sum) fd.append("min_insurance_sum", form.min_insurance_sum);
      fd.append("requires_weekly_reports", String(form.requires_weekly_reports));
      fd.append("report_template", form.report_template);
      if (form.report_day_of_week !== "") fd.append("report_day_of_week", form.report_day_of_week);
      if (form.report_due_time) fd.append("report_due_time", form.report_due_time);
      if (docFile) fd.append("document_file", docFile);
      if (pricingFile) fd.append("pricing_file", pricingFile);
      return apiClient.patch(`/agreements/${agreement.id}/`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["agreement", agreement.id] });
      qc.invalidateQueries({ queryKey: ["agreements"] });
      setDocFile(null);
      setPricingFile(null);
      onClose();
    },
    onError: () => setFormErrors({ _: "Błąd zapisu. Sprawdź dane." }),
  });

  const validate = (): boolean => {
    const errs: Record<string, string> = {};
    if (!form.title.trim()) errs.title = "Tytuł jest wymagany";
    if (!form.signed_date) errs.signed_date = "Data podpisania jest wymagana";
    if (form.has_min_order_amount && !(parseFloat(form.min_order_amount) > 0))
      errs.min_order_amount = "Podaj kwotę minimalną zamówienia większą od zera";
    const acceptanceError = acceptanceDeadlineDaysError(form.acceptance_deadline_days);
    if (acceptanceError) errs.acceptance_deadline_days = acceptanceError;
    setFormErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const upd = <K extends keyof EditAgreementForm>(
    k: K,
    v: EditAgreementForm[K],
  ) => setForm((p) => ({ ...p, [k]: v }));

  return (
    <Modal open={open} onClose={onClose} title="Edytuj umowę ramową" size="md">
      <div className="flex flex-col gap-4">
        <div>
          <label className="text-sm font-medium text-gray-700 dark:text-gray-300 block mb-1">
            Tytuł umowy<span className="text-red-500 ml-0.5">*</span>
          </label>
          <input
            type="text"
            className={`block w-full rounded-md border px-3 py-2 text-sm ${formErrors.title ? "border-red-400" : "border-gray-300"}`}
            value={form.title}
            onChange={(e) => upd("title", e.target.value)}
          />
          {formErrors.title && (
            <p className="text-xs text-red-600 mt-0.5">{formErrors.title}</p>
          )}
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="text-sm font-medium text-gray-700 dark:text-gray-300 block mb-1">
              Data podpisania<span className="text-red-500 ml-0.5">*</span>
            </label>
            <DatePicker
              className={formErrors.signed_date ? "border-red-400" : undefined}
              value={form.signed_date}
              onChange={(v) => upd("signed_date", v)}
            />
            {formErrors.signed_date && (
              <p className="text-xs text-red-600 mt-0.5">
                {formErrors.signed_date}
              </p>
            )}
          </div>
          <div>
            <label className="text-sm font-medium text-gray-700 dark:text-gray-300 block mb-1">
              Ważna do (opcjonalnie)
            </label>
            <DatePicker
              value={form.valid_until}
              onChange={(v) => upd("valid_until", v)}
            />
          </div>
        </div>
        <div>
          <label className="text-sm font-medium text-gray-700 dark:text-gray-300 block mb-1">
            Termin płatności (dni od wystawienia POCK)
          </label>
          <input
            type="number"
            min="0"
            placeholder="np. 30"
            className="block w-40 rounded-md border border-gray-300 px-3 py-2 text-sm"
            value={form.payment_term_days}
            onChange={(e) => upd("payment_term_days", e.target.value)}
          />
          <p className="text-xs text-gray-400 mt-0.5">
            Używany do planowania wydatków POCK w cashflow
          </p>
        </div>
        <AcceptanceDeadlineDaysField
          value={form.acceptance_deadline_days}
          onChange={(v) => upd("acceptance_deadline_days", v)}
          error={formErrors.acceptance_deadline_days}
        />
        <label className="flex items-center gap-2 cursor-pointer">
          <input
            type="checkbox"
            checked={form.is_confidential}
            onChange={(e) => upd("is_confidential", e.target.checked)}
            className="rounded"
          />
          <span className="text-sm text-gray-700 dark:text-gray-300">
            Umowa poufna (widoczna tylko dla Dyrektora)
          </span>
        </label>
        <label className="flex items-center gap-2 cursor-pointer">
          <input
            type="checkbox"
            checked={form.auto_accept}
            onChange={(e) => upd("auto_accept", e.target.checked)}
            className="rounded"
          />
          <span className="text-sm text-gray-700 dark:text-gray-300">
            Automatyczne przyjęcie zleceń (podwykonawca dostaje tylko
            powiadomienie)
          </span>
        </label>
        <label className="flex items-center gap-2 cursor-pointer">
          <input
            type="checkbox"
            checked={form.allow_partial_pock}
            onChange={(e) => upd("allow_partial_pock", e.target.checked)}
            className="rounded"
          />
          <span className="text-sm text-gray-700 dark:text-gray-300">
            Zezwalaj na częściowy POCK (odznacz, aby wymagać 100% zakresu przy
            każdym wniosku)
          </span>
        </label>
        <label className="flex items-center gap-2 cursor-pointer">
          <input
            type="checkbox"
            checked={form.allow_external_costs}
            onChange={(e) => upd("allow_external_costs", e.target.checked)}
            className="rounded"
          />
          <span className="text-sm text-gray-700 dark:text-gray-300">
            Zezwalaj na koszty zewnętrzne (podwykonawca może składać wnioski)
          </span>
        </label>
        <label className="flex items-center gap-2 cursor-pointer">
          <input
            type="checkbox"
            checked={form.allow_official_fee_requests}
            onChange={(e) => upd("allow_official_fee_requests", e.target.checked)}
            className="rounded"
          />
          <span className="text-sm text-gray-700 dark:text-gray-300">
            Zezwalaj na opłaty urzędowe (podwykonawca może wnioskować o opłatę urzędową)
          </span>
        </label>
        <div>
          <label className="flex items-center gap-2 cursor-pointer mb-2">
            <input
              type="checkbox"
              checked={form.has_min_order_amount}
              onChange={(e) => upd("has_min_order_amount", e.target.checked)}
              className="rounded"
            />
            <span className="text-sm text-gray-700 dark:text-gray-300">
              Kwota minimalna zamówienia
            </span>
          </label>
          {form.has_min_order_amount && (
            <div className="ml-6">
              <label className="text-xs font-medium text-gray-600 dark:text-gray-400 block mb-1">
                Kwota minimalna (PLN)
              </label>
              <input
                type="number"
                min="0"
                step="0.01"
                placeholder="np. 5000"
                className={`block w-48 rounded-md border px-3 py-1.5 text-sm ${formErrors.min_order_amount ? "border-red-400" : "border-gray-300"}`}
                value={form.min_order_amount}
                onChange={(e) => upd("min_order_amount", e.target.value)}
              />
              {formErrors.min_order_amount && (
                <p className="text-xs text-red-600 mt-0.5">{formErrors.min_order_amount}</p>
              )}
              <p className="text-xs text-gray-400 mt-0.5">
                Zapotrzebowanie o niższej wartości dostanie ostrzeżenie przy proponowaniu podwykonawcy i zatwierdzaniu (nie blokuje).
              </p>
            </div>
          )}
        </div>
        <div>
          <label className="flex items-center gap-2 cursor-pointer mb-2">
            <input
              type="checkbox"
              checked={form.requires_insurance}
              onChange={(e) => upd("requires_insurance", e.target.checked)}
              className="rounded"
            />
            <span className="text-sm text-gray-700 dark:text-gray-300">
              Wymagana polisa OC
            </span>
          </label>
          {form.requires_insurance && (
            <div className="ml-6">
              <label className="text-xs font-medium text-gray-600 dark:text-gray-400 block mb-1">
                Minimalna suma ubezpieczenia (PLN, opcjonalnie)
              </label>
              <input
                type="number"
                min="0"
                placeholder="np. 1000000"
                className="block w-48 rounded-md border border-gray-300 px-3 py-1.5 text-sm"
                value={form.min_insurance_sum}
                onChange={(e) => upd("min_insurance_sum", e.target.value)}
              />
            </div>
          )}
        </div>
        <div>
          <label className="flex items-center gap-2 cursor-pointer mb-2">
            <input
              type="checkbox"
              checked={form.requires_weekly_reports}
              onChange={(e) => upd("requires_weekly_reports", e.target.checked)}
              className="rounded"
            />
            <span className="text-sm text-gray-700 dark:text-gray-300">
              Wymagaj cotygodniowych raportów postępu
            </span>
          </label>
          {form.requires_weekly_reports && (
            <div className="ml-6 flex flex-col gap-3">
              <div>
                <label className="text-xs font-medium text-gray-600 dark:text-gray-400 block mb-1">
                  Typ raportu
                </label>
                <select
                  className="block rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-3 py-1.5 text-sm"
                  value={form.report_template}
                  onChange={(e) =>
                    upd("report_template", e.target.value as "standard" | "complex_lot")
                  }
                >
                  <option value="standard">Standardowy</option>
                </select>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs font-medium text-gray-600 dark:text-gray-400 block mb-1">
                    Dzień składania raportu
                  </label>
                  <select
                    className="block w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-3 py-1.5 text-sm"
                    value={form.report_day_of_week}
                    onChange={(e) => upd("report_day_of_week", e.target.value)}
                  >
                    <option value="">— bez określenia —</option>
                    {[
                      "Poniedziałek",
                      "Wtorek",
                      "Środa",
                      "Czwartek",
                      "Piątek",
                      "Sobota",
                      "Niedziela",
                    ].map((d, i) => (
                      <option key={i} value={String(i)}>
                        {d}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="text-xs font-medium text-gray-600 dark:text-gray-400 block mb-1">
                    Godzina deadline
                  </label>
                  <input
                    type="time"
                    className="block w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-3 py-1.5 text-sm"
                    value={form.report_due_time}
                    onChange={(e) => upd("report_due_time", e.target.value)}
                  />
                </div>
              </div>
            </div>
          )}
        </div>
        {canManageDoc && (
          <div>
            <label className="text-sm font-medium text-gray-700 dark:text-gray-300 block mb-1">
              Dokument umowy (PDF)
            </label>
            {agreement.document_managed_file_detail && !docFile && (
              <div className="flex items-center gap-2 mb-2">
                <a
                  href={agreement.document_managed_file_detail.url ?? undefined}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center gap-1 text-sm text-[var(--color-primary)] hover:underline"
                >
                  <FileText size={13} />
                  {agreement.document_managed_file_detail.original_filename}
                  <ExternalLink size={11} />
                </a>
                <span className="text-xs text-gray-400">(aktualny)</span>
              </div>
            )}
            <FileDropZone
              files={docFile ? [docFile] : []}
              onFilesChange={(fs) => setDocFile(fs[0] ?? null)}
              accept=".pdf,.doc,.docx"
              label={
                agreement.document_managed_file_detail
                  ? "Przeciągnij nowy plik lub kliknij, aby zamienić"
                  : undefined
              }
            />
          </div>
        )}
        {canManageDoc && (
          <div>
            <label className="text-sm font-medium text-gray-700 dark:text-gray-300 block mb-1">
              Cennik umowy ramowej
            </label>
            {agreement.pricing_managed_file_detail && !pricingFile && (
              <div className="flex items-center gap-2 mb-2">
                <ManagedFileLink
                  file={agreement.pricing_managed_file_detail}
                  icon={<FileText size={13} />}
                  className="text-sm"
                >
                  {agreement.pricing_managed_file_detail.original_filename}
                </ManagedFileLink>
                <span className="text-xs text-gray-400">(aktualny)</span>
              </div>
            )}
            <FileDropZone
              files={pricingFile ? [pricingFile] : []}
              onFilesChange={(fs) => setPricingFile(fs[0] ?? null)}
              accept=".pdf,.doc,.docx,.xls,.xlsx"
              label={
                agreement.pricing_managed_file_detail
                  ? "Przeciągnij nowy plik lub kliknij, aby zamienić"
                  : undefined
              }
            />
            <p className="text-xs text-gray-400 mt-0.5">
              Widoczny dla obu stron umowy — także dla podwykonawcy
            </p>
          </div>
        )}
        {formErrors._ && <p className="text-sm text-red-600">{formErrors._}</p>}
        <div className="flex gap-3 pt-1">
          <Button
            loading={saveMutation.isPending}
            onClick={() => {
              if (validate()) saveMutation.mutate();
            }}
          >
            Zapisz zmiany
          </Button>
          <Button
            variant="secondary"
            onClick={() => {
              setDocFile(null);
              setPricingFile(null);
              onClose();
            }}
          >
            Anuluj
          </Button>
        </div>
      </div>
    </Modal>
  );
}

// ── Verbal ('słowne') positions for AI valuation ────────────────────────────────

interface VerbalRule {
  id: string;
  contractor_name: string | null;
  label: string;
  rule_text: string;
  value_multiplier: string | null;
  travel_free_km: string | null;
  is_active: boolean;
}

function VerbalRulesSection({
  agreementId,
  isDirector,
}: {
  agreementId: string;
  isDirector: boolean;
}) {
  const qc = useQueryClient();
  const [showAdd, setShowAdd] = useState(false);
  const [label, setLabel] = useState("");
  const [ruleText, setRuleText] = useState("");
  const [mult, setMult] = useState("");
  const [freeKm, setFreeKm] = useState("");
  const [err, setErr] = useState("");
  const key = ["agreement-verbal-rules", agreementId];

  const { data: rules = [] } = useQuery({
    queryKey: key,
    queryFn: () =>
      apiClient
        .get<{ data: VerbalRule[] }>(
          `/estimating/framework-agreements/${agreementId}/verbal-rules/`,
        )
        .then((r) => r.data.data),
  });

  const reset = () => {
    setLabel("");
    setRuleText("");
    setMult("");
    setFreeKm("");
    setErr("");
    setShowAdd(false);
  };

  const create = useMutation({
    mutationFn: () =>
      apiClient.post(
        `/estimating/framework-agreements/${agreementId}/verbal-rules/`,
        {
          label,
          rule_text: ruleText,
          value_multiplier: mult || null,
          travel_free_km: freeKm || null,
        },
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: key });
      reset();
    },
    onError: (e: any) =>
      setErr(
        e?.response?.data?.errors?.[0] ||
          "Błąd zapisu. Sprawdź dane (nazwa i treść wymagane).",
      ),
  });

  const toggleActive = useMutation({
    mutationFn: (r: VerbalRule) =>
      apiClient.patch(`/estimating/verbal-rules/${r.id}/`, {
        is_active: !r.is_active,
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: key }),
  });

  const remove = useMutation({
    mutationFn: (rid: string) =>
      apiClient.delete(`/estimating/verbal-rules/${rid}/`),
    onSuccess: () => qc.invalidateQueries({ queryKey: key }),
  });

  const inp =
    "w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-3 py-2 text-sm";

  return (
    <section className="mb-6">
      <div className="flex items-center justify-between mb-1">
        <h2 className="text-base font-semibold text-gray-900 dark:text-gray-100">
          Pozycje słowne dla wyceny AI ({rules.length})
        </h2>
        {isDirector && (
          <button
            className="inline-flex items-center gap-1 text-sm text-[var(--color-primary)] hover:underline"
            onClick={() => {
              setShowAdd((s) => !s);
              setErr("");
            }}
          >
            <Plus size={14} /> Dodaj słowną
          </button>
        )}
      </div>
      <p className="text-xs text-gray-500 dark:text-gray-400 mb-3">
        Dogadany deal opisany własnymi słowami — przy wycenie AI obejmie pasujące pozycje
        kosztorysu i policzy je <strong>formułą od naszej wartości ofertowej</strong>{" "}
        (np. „minus 5%” → ×0,95), bez wpisywania pozycja po pozycji. Mnożnik i próg dojazdu
        możesz zostawić puste — AI odczyta je z treści.
      </p>

      {showAdd && (
        <div className="mb-3 rounded-xl border border-[var(--color-primary)]/40 bg-[var(--color-primary)]/5 p-4 space-y-3">
          <div>
            <label className="block text-xs text-gray-600 dark:text-gray-300 mb-1">
              Nazwa pozycji słownej <span className="text-red-500">*</span>
            </label>
            <input
              className={inp}
              value={label}
              onChange={(e) => setLabel(e.target.value)}
              placeholder='np. "Roboty około-elektryczne (PPN)"'
            />
          </div>
          <div>
            <label className="block text-xs text-gray-600 dark:text-gray-300 mb-1">
              Treść dealu (dla AI) <span className="text-red-500">*</span>
            </label>
            <textarea
              className={inp}
              rows={3}
              value={ruleText}
              onChange={(e) => setRuleText(e.target.value)}
              placeholder="np. Robi nam roboty około-elektryczne (podłączenia, zarobienia końców, mufy, pomiary) na stawkach SEKOCENBUD średnich, narzut 68%, minus 5% od naszej wartości. Dojazd darmowy do 50 km, dalej per km."
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs text-gray-600 dark:text-gray-300 mb-1">
                Mnożnik wartości (opcj.)
              </label>
              <input
                className={inp}
                value={mult}
                onChange={(e) => setMult(e.target.value)}
                placeholder="np. 0,95 (=−5%)"
              />
            </div>
            <div>
              <label className="block text-xs text-gray-600 dark:text-gray-300 mb-1">
                Dojazd darmowy do [km] (opcj.)
              </label>
              <input
                className={inp}
                value={freeKm}
                onChange={(e) => setFreeKm(e.target.value)}
                placeholder="np. 50"
              />
            </div>
          </div>
          {err && <p className="text-xs text-red-500">{err}</p>}
          <div className="flex gap-2">
            <Button
              size="sm"
              loading={create.isPending}
              onClick={() => create.mutate()}
            >
              Zapisz
            </Button>
            <Button size="sm" variant="secondary" onClick={reset}>
              Anuluj
            </Button>
          </div>
        </div>
      )}

      {rules.length === 0 ? (
        <p className="text-sm text-gray-400 dark:text-gray-500">
          Brak pozycji słownych.
        </p>
      ) : (
        <div className="space-y-2">
          {rules.map((r) => (
            <div
              key={r.id}
              className={`rounded-lg border p-3 ${
                r.is_active
                  ? "border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800"
                  : "border-dashed border-gray-300 dark:border-gray-600 opacity-60"
              }`}
            >
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <div className="text-sm font-medium text-gray-900 dark:text-gray-100">
                    {r.label}
                    {!r.is_active && (
                      <span className="ml-2 text-[10px] uppercase tracking-wide text-gray-400">
                        nieaktywna
                      </span>
                    )}
                  </div>
                  <p className="mt-0.5 text-xs text-gray-600 dark:text-gray-300">
                    {r.rule_text}
                  </p>
                  <div className="mt-1 flex flex-wrap gap-2 text-[11px] text-gray-500 dark:text-gray-400">
                    {r.value_multiplier != null && (
                      <span>
                        formuła ×{String(r.value_multiplier).replace(".", ",")}
                      </span>
                    )}
                    {r.travel_free_km != null && (
                      <span>
                        dojazd darmowy do {String(r.travel_free_km).replace(".", ",")} km
                      </span>
                    )}
                    {r.value_multiplier == null && r.travel_free_km == null && (
                      <span>mnożnik/dojazd: z treści (AI)</span>
                    )}
                  </div>
                </div>
                {isDirector && (
                  <div className="flex shrink-0 items-center gap-2">
                    <button
                      className="text-[11px] text-gray-500 hover:underline"
                      onClick={() => toggleActive.mutate(r)}
                    >
                      {r.is_active ? "wyłącz" : "włącz"}
                    </button>
                    <button
                      className="text-gray-400 hover:text-red-500"
                      onClick={() => remove.mutate(r.id)}
                      title="Usuń"
                    >
                      <Trash2 size={14} />
                    </button>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </section>
  );
}

// ── Dokument umowy ────────────────────────────────────────────────────────────

/**
 * The scan of the agreement itself. Renders the state EXPLICITLY: before this the
 * card simply did not exist when no file had been uploaded, which read as "the
 * document is hidden from me" (zgłoszenie „Umowy - podwykonawcy", 28.08.2026) —
 * while nothing had ever been uploaded for 24 of 35 agreements.
 *
 * Replacing the file keeps the previous one: every upload is recorded as an
 * AgreementFileVersion, so what was swapped out stays downloadable below.
 *
 * Rendered only for users who may manage agreements — the scan is director-only
 * (2026-05-08) and the backend serves it to nobody else, so showing this card to
 * anyone else would state "brak skanu" about a file that exists.
 */
function AgreementDocumentSection({ agreement }: { agreement: Agreement }) {
  const qc = useQueryClient();
  const [file, setFile] = useState<File | null>(null);
  const [error, setError] = useState<string | null>(null);

  const current = agreement.document_managed_file_detail;
  const versions = agreement.document_file_versions ?? [];
  const previous = versions.filter(
    (v) => v.managed_file && v.managed_file !== agreement.document_managed_file,
  );

  const upload = useMutation({
    mutationFn: (picked: File) => {
      const fd = new FormData();
      fd.append("document_file", picked);
      return apiClient.patch(`/agreements/${agreement.id}/`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
    },
    onSuccess: () => {
      setFile(null);
      setError(null);
      qc.invalidateQueries({ queryKey: ["agreement", agreement.id] });
      qc.invalidateQueries({ queryKey: ["agreements"] });
    },
    onError: () => setError("Nie udało się wgrać pliku. Spróbuj ponownie."),
  });

  return (
    <div className="mb-6 rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-4">
      <h3 className="text-sm font-semibold text-gray-800 dark:text-gray-200 mb-2 flex items-center gap-2">
        <FileText size={15} className="text-gray-400 dark:text-gray-500" />
        Dokument umowy
      </h3>

      {current ? (
        <div className="flex items-center gap-3 flex-wrap">
          <span className="text-sm font-medium text-gray-900 dark:text-gray-100">
            {current.original_filename}
          </span>
          <ManagedFileLink file={current} icon={<FileText size={12} />}>
            Pobierz umowę
          </ManagedFileLink>
        </div>
      ) : (
        <p className="text-sm text-gray-500 dark:text-gray-400">
          Brak wgranego skanu umowy — nie ma czego pobrać. Wgraj plik poniżej.
        </p>
      )}

      <div className="mt-3 border-t border-gray-100 dark:border-gray-700 pt-3">
        <FileDropZone
          files={file ? [file] : []}
          onFilesChange={(fs) => {
            setFile(fs[0] ?? null);
            setError(null);
          }}
          accept=".pdf,.doc,.docx"
          disabled={upload.isPending}
          label={
            current
              ? "Przeciągnij nowy plik lub kliknij, aby zamienić"
              : "Przeciągnij skan umowy lub kliknij, aby wybrać"
          }
        />
        <div className="flex items-center gap-3 mt-2 flex-wrap">
          <Button
            size="sm"
            disabled={!file || upload.isPending}
            onClick={() => file && upload.mutate(file)}
          >
            <Upload size={13} className="mr-1" />
            {upload.isPending ? "Wgrywanie…" : current ? "Zamień plik" : "Wgraj plik"}
          </Button>
          {current && (
            <span className="text-xs text-gray-400 dark:text-gray-500">
              Poprzedni plik zostaje w historii poniżej.
            </span>
          )}
          {error && <span className="text-xs text-red-600">{error}</span>}
        </div>
      </div>

      {versions.length > 0 && (
        <div className="mt-3 border-t border-gray-100 dark:border-gray-700 pt-3">
          <p className="text-xs font-semibold text-gray-500 dark:text-gray-400 mb-1.5">
            Historia pliku ({versions.length})
            {previous.length === 0 && " — brak wcześniejszych wersji"}
          </p>
          <ul className="flex flex-col gap-1">
            {versions.map((v) => {
              const isCurrent =
                !!v.managed_file && v.managed_file === agreement.document_managed_file;
              return (
                <li
                  key={v.id}
                  className="flex items-center gap-2 flex-wrap text-xs text-gray-600 dark:text-gray-400"
                >
                  <span className="text-gray-400 dark:text-gray-500 whitespace-nowrap">
                    {new Date(v.created_at).toLocaleString("pl")}
                  </span>
                  <span className="truncate max-w-[22rem]">
                    {v.managed_file_detail?.original_filename ?? "plik usunięty"}
                  </span>
                  {v.uploaded_by_name && (
                    <span className="text-gray-400 dark:text-gray-500">
                      · {v.uploaded_by_name}
                    </span>
                  )}
                  {isCurrent && <Badge color="green">aktualny</Badge>}
                  <ManagedFileLink
                    file={v.managed_file_detail}
                    icon={<FileText size={11} />}
                  >
                    Pobierz
                  </ManagedFileLink>
                </li>
              );
            })}
          </ul>
        </div>
      )}
    </div>
  );
}

// ── Main page ──────────────────────────────────────────────────────────────────

/**
 * Usunięcie umowy ramowej — nieodwracalne, więc pytamy wprost i wyliczamy, co zniknie.
 *
 * Przycisk widzi tylko autor umowy i dyrektor (flaga `can_delete` z backendu, który
 * sprawdza to samo jeszcze raz przy DELETE). Umowy, na której już coś zrobiono,
 * backend nie skasuje — od zamykania współpracy jest data ważności i wycofywanie
 * pozycji z cennika.
 */
function DeleteAgreementDialog({
  agreement,
  open,
  onClose,
}: {
  agreement: Agreement;
  open: boolean;
  onClose: () => void;
}) {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [error, setError] = useState("");

  const deleteMutation = useMutation({
    mutationFn: () => apiClient.delete(`/agreements/${agreement.id}/`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["agreements"] });
      qc.invalidateQueries({
        queryKey: ["contractors", agreement.contractor, "history"],
      });
      navigate("/agreements");
    },
    onError: (err: unknown) => {
      const detail = (
        err as { response?: { data?: { errors?: unknown } } }
      )?.response?.data?.errors;
      setError(
        typeof detail === "string"
          ? detail
          : "Nie udało się usunąć umowy. Odśwież stronę i spróbuj ponownie."
      );
    },
  });

  return (
    <Modal open={open} onClose={onClose} title="Usuń umowę ramową" size="md">
      <div className="p-6">
        <div className="flex gap-3 rounded-lg border border-red-200 bg-red-50 p-3 dark:border-red-900 dark:bg-red-900/20">
          <AlertTriangle size={18} className="mt-0.5 shrink-0 text-red-500" />
          <div className="text-sm text-red-800 dark:text-red-300">
            <p className="font-semibold">
              Umowa „{agreement.title}” ({agreement.contractor_name}) zniknie
              bezpowrotnie.
            </p>
            <p className="mt-1">
              Razem z nią kasujemy cennik ({agreement.line_item_types.length}{" "}
              pozycji), zapisane kary umowne, historię wgranych plików, inne
              pliki umowy oraz wewnętrzne konwersacje przy niej. Skanów nie da
              się później odzyskać z poziomu aplikacji.
            </p>
          </div>
        </div>

        <p className="mt-4 text-sm text-gray-600 dark:text-gray-400">
          Jeśli chodzi Ci o zakończenie współpracy, a nie o pomyłkę przy
          zakładaniu — ustaw datę ważności umowy albo wycofaj pozycje z cennika.
          Stare zamówienia zostaną wtedy nietknięte.
        </p>

        {error && (
          <p className="mt-3 rounded border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700 dark:border-red-900 dark:bg-red-900/20 dark:text-red-300">
            {error}
          </p>
        )}

        <div className="mt-6 flex justify-end gap-2">
          <Button variant="secondary" onClick={onClose}>
            Anuluj
          </Button>
          <Button
            variant="danger"
            loading={deleteMutation.isPending}
            onClick={() => {
              setError("");
              deleteMutation.mutate();
            }}
          >
            Usuń umowę
          </Button>
        </div>
      </div>
    </Modal>
  );
}


export function AgreementDetailPage() {
  const { id } = useParams<{ id: string }>();
  const user = useAuthStore((s) => s.user);
  // May edit agreements: CEO / technical director by role, or the CEO-granted
  // 'Podwykonawcy i umowy ramowe' permission (mirrors backend CanManageContractors).
  // Sub-components still receive it under the historical `isDirector` prop name.
  const isDirector = canManageContractors(user);
  const canManageDoc = isDirector;
  // Grupy umów to osobne, węższe uprawnienie (backend CanManageAgreementGroups).
  const canManageGroups = canManageAgreementGroups(user);
  // Konwersacje chodzą po ROLI, nie po grancie „Podwykonawcy i umowy ramowe":
  // to wewnętrzna rozmowa dyrekcji o współpracy, a nie zarządzanie umową.
  const isDirectorRole = user?.role === "director";
  const isCeo = Boolean(user?.is_ceo);
  const [showEdit, setShowEdit] = useState(false);
  const [showDelete, setShowDelete] = useState(false);
  const [activeTab, setActiveTab] = useState<
    "history" | "invoices" | "penalties"
  >("history");

  const { data: agreement, isLoading } = useQuery({
    queryKey: ["agreement", id],
    queryFn: () =>
      apiClient
        .get<{ data: Agreement }>(`/agreements/${id}/`)
        .then((r) => r.data.data),
    enabled: !!id,
  });
  usePageTitle(agreement && `Umowa: ${agreement.title}`);
  // Dzwonek z konwersacji linkuje w „…?tab=conversation&comment=<id>" — doscrollujmy
  // tam, zamiast zostawiać czytającego na górze długiej karty umowy.
  const { ref: conversationsRef } = useConversationDeepLink(!isLoading);

  const { data: summary } = useQuery({
    queryKey: ["agreement-summary", id],
    queryFn: () =>
      apiClient
        .get<{ data: AgreementSummary }>(`/agreements/${id}/summary/`)
        .then((r) => r.data.data),
    enabled: !!id && canManageDoc,
  });

  const { data: penaltyRecords = [] } = useQuery({
    queryKey: ["agreement-penalty-records", id],
    queryFn: () =>
      apiClient
        .get<{ data: PenaltyRecord[] }>(`/agreements/${id}/penalty-records/`)
        .then((r) => r.data.data),
    enabled: !!id,
  });

  const qcMain = useQueryClient();

  const contractorId = agreement?.contractor;
  const { data: ocPolicies = [] } = useQuery({
    queryKey: ["oc-policies", contractorId],
    queryFn: () =>
      apiClient
        .get<{ data: OCPolicy[] }>(`/contractors/${contractorId}/oc-policies/`)
        .then((r) => r.data.data),
    enabled: !!contractorId,
  });

  const [ocForm, setOcForm] = useState({ valid_until: "", insurance_sum: "" });
  const [ocFile, setOcFile] = useState<File | null>(null);
  const [ocUploadError, setOcUploadError] = useState("");

  const uploadOcMutation = useMutation({
    mutationFn: () => {
      if (!ocFile) throw new Error("Brak pliku");
      const fd = new FormData();
      fd.append("contractor", contractorId!);
      fd.append("policy_file", ocFile);
      fd.append("insurance_sum", ocForm.insurance_sum);
      fd.append("valid_until", ocForm.valid_until);
      return apiClient.post(`/contractors/${contractorId}/oc-policies/`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
    },
    onSuccess: () => {
      qcMain.invalidateQueries({ queryKey: ["oc-policies", contractorId] });
      qcMain.invalidateQueries({ queryKey: ["agreement", id] });
      setOcForm({ valid_until: "", insurance_sum: "" });
      setOcFile(null);
      setOcUploadError("");
    },
    onError: () => setOcUploadError("Nie udało się wgrać polisy."),
  });

  if (isLoading) return <PageLoader />;
  if (!agreement)
    return (
      <p className="text-gray-500 dark:text-gray-400">Nie znaleziono umowy.</p>
    );

  const isExpired =
    !!agreement.valid_until && new Date(agreement.valid_until) < new Date();

  return (
    <div className="max-w-5xl">
      <Link
        to="/agreements"
        className="inline-flex items-center gap-1 text-sm text-gray-500 dark:text-gray-400 hover:text-gray-800 mb-5"
      >
        <ArrowLeft size={14} /> Umowy ramowe
      </Link>

      {/* Header */}
      <div className="flex items-start justify-between mb-5">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">
            {agreement.title}
          </h1>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">
            <Link
              to={`/contractors/${agreement.contractor}`}
              className="hover:underline"
            >
              {agreement.contractor_name}
            </Link>
            {" · "}
            Podpisano:{" "}
            {new Date(agreement.signed_date).toLocaleDateString("pl")}
            {agreement.valid_until && (
              <>
                {" · "}
                Ważna do:{" "}
                {new Date(agreement.valid_until).toLocaleDateString("pl")}
              </>
            )}
            {" · "}
            Termin płatności:{" "}
            <strong>
              {agreement.payment_term_days != null
                ? `${agreement.payment_term_days} dni`
                : "—"}
            </strong>
            {" · "}
            Termin na przyjęcie zamówienia:{" "}
            <strong data-testid="agreement-acceptance-deadline-days">
              {agreement.auto_accept
                ? "nie dotyczy (automatyczne przyjęcie)"
                : acceptanceDeadlineDaysLabel(agreement.acceptance_deadline_days)}
            </strong>
            {agreement.min_order_amount != null && (
              <>
                {" · "}
                Kwota minimalna zamówienia:{" "}
                <strong>{formatAmount(agreement.min_order_amount)} PLN</strong>
              </>
            )}
            {agreement.created_by_name && (
              <>
                {" · "}
                Założył: {agreement.created_by_name}
              </>
            )}
          </p>
        </div>
        <div className="flex items-center gap-2">
          {isExpired ? (
            <Badge color="red">Wygasła</Badge>
          ) : (
            <Badge color="green">Aktywna</Badge>
          )}
          {agreement.is_confidential && <Badge color="gray">Poufna</Badge>}
          {isDirector && (
            <Badge color={agreement.allow_partial_pock ? "gray" : "yellow"}>
              {agreement.allow_partial_pock
                ? "Częściowy POCK: tak"
                : "Tylko końcowy POCK"}
            </Badge>
          )}
          {canManageDoc && agreement.requires_insurance && (
            <Badge
              color={
                agreement.insurance_status === "valid"
                  ? "green"
                  : agreement.insurance_status === "expiring_soon"
                    ? "yellow"
                    : agreement.insurance_status === "expired"
                      ? "red"
                      : "gray"
              }
            >
              {agreement.insurance_status === "valid"
                ? "Polisa OC: OK"
                : agreement.insurance_status === "expiring_soon"
                  ? "Polisa OC: wygasa"
                  : agreement.insurance_status === "expired"
                    ? "Polisa OC: WYGASŁA"
                    : "Polisa OC: brak daty"}
            </Badge>
          )}
          {canManageDoc && agreement.document_managed_file_detail?.url && (
            <a
              href={agreement.document_managed_file_detail.url}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-1.5 rounded-md border border-gray-300 bg-white dark:bg-gray-800 px-3 py-1.5 text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700/50"
              title={agreement.document_managed_file_detail.original_filename}
            >
              <FileText size={13} /> Pobierz PDF <ExternalLink size={11} />
            </a>
          )}
          {canManageDoc && (
            <button
              onClick={() => setShowEdit(true)}
              className="inline-flex items-center gap-1.5 rounded-md border border-gray-300 bg-white dark:bg-gray-800 px-3 py-1.5 text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700/50"
            >
              <Pencil size={13} /> Edytuj
            </button>
          )}
          {/* Kasuje tylko autor umowy i dyrektor — backend rozstrzyga to w can_delete.
              Umowa już użyta zostaje: przycisk jest wtedy nieaktywny i mówi dlaczego. */}
          {agreement.can_delete && (
            <button
              onClick={() => setShowDelete(true)}
              disabled={!!agreement.delete_blocked_reason}
              title={
                agreement.delete_blocked_reason ??
                "Usuń umowę ramową (nieodwracalne)"
              }
              className="inline-flex items-center gap-1.5 rounded-md border border-red-300 bg-white dark:bg-gray-800 px-3 py-1.5 text-sm font-medium text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:bg-white dark:disabled:hover:bg-gray-800"
            >
              <Trash2 size={13} /> Usuń
            </button>
          )}
        </div>
      </div>

      {/* Summary cards — visible to directors and CEO only */}
      {canManageDoc && summary && (
        <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-5 gap-3 mb-6">
          <SummaryCard
            label="Wartość zamówień"
            value={`${formatAmount(summary.total_orders_value)} PLN`}
            sub={`${summary.orders_count} zamówień (${summary.active_orders_count} aktywnych)`}
          />
          <SummaryCard
            label="Ocena podwykonawcy"
            value={summary.rating != null ? `${summary.rating.toFixed(2)} / 5` : "—"}
            sub={
              summary.rating != null
                ? `${summary.ratings_count} ${pluralOcena(summary.ratings_count)}`
                : "brak ocen"
            }
            color={summary.rating != null ? "text-amber-600 dark:text-amber-400" : undefined}
          />
          <SummaryCard
            label="Zafakturowano netto"
            value={`${formatAmount(summary.invoiced_net)} PLN`}
          />
          <SummaryCard
            label="Zapłacono"
            value={`${formatAmount(summary.paid_net)} PLN`}
            color="text-green-700"
          />
          <SummaryCard
            label="Do zapłaty"
            value={`${formatAmount(summary.pending_net)} PLN`}
            color={
              parseFloat(summary.pending_net) > 0
                ? "text-amber-700"
                : "text-gray-900 dark:text-gray-100"
            }
          />
        </div>
      )}
      {canManageDoc && summary && parseFloat(summary.penalties_total) > 0 && (
        <div className="rounded-xl border border-red-200 bg-red-50 px-5 py-3 mb-6 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <AlertTriangle size={16} className="text-red-500" />
            <span className="text-sm font-semibold text-red-800">
              Naliczone kary umowne łącznie:
            </span>
          </div>
          <span className="text-lg font-bold text-red-700">
            {formatAmount(summary.penalties_total)} PLN
          </span>
        </div>
      )}

      {/* Grupy umowy — po nich wycena kosztorysu rozwija listę podwykonawców.
          Widzi każdy pracownik; przypina osoba z uprawnieniem „Grupy umów ramowych". */}
      {user?.role !== "contractor" && (
        <AgreementGroupsSection
          agreementId={agreement.id}
          groups={agreement.groups ?? []}
          canManage={canManageGroups}
        />
      )}

      {/* Informacje dla koordynatorów — notatka dla zamawiających (ikona „?" na megamapie);
          edytuje ten sam krąg, co umowę. Konto podwykonawcy pola nie dostaje. */}
      {user?.role !== "contractor" && (
        <AgreementCoordinatorInfoSection
          agreementId={agreement.id}
          value={agreement.coordinator_info ?? ""}
          canManage={isDirector}
        />
      )}

      {/* Dokument umowy — plik umowy jest sprawą dyrekcji (decyzja z 08.05.2026),
          więc sekcja istnieje dla tych, którzy mogą go pobrać i podmienić. Dla nich
          stan jest JAWNY: „jest plik" albo „brak skanu", nigdy pusto. */}
      {canManageDoc && <AgreementDocumentSection agreement={agreement} />}

      {/* Inne pliki umowy — aneksy i załączniki; ta sama bramka co skan umowy */}
      {canManageDoc && (
        <AgreementExtraFilesSection agreementId={agreement.id} canManage={canManageDoc} />
      )}

      {/* Cennik — uploaded by the director, readable by both parties */}
      <div className="mb-6 rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-4">
        <h3 className="text-sm font-semibold text-gray-800 dark:text-gray-200 mb-2 flex items-center gap-2">
          <FileText size={15} className="text-gray-400 dark:text-gray-500" />
          Cennik umowy ramowej
        </h3>
        {agreement.pricing_managed_file_detail ? (
          <div className="flex items-center gap-3 flex-wrap">
            <span className="text-sm font-medium text-gray-900 dark:text-gray-100">
              {agreement.pricing_managed_file_detail.original_filename}
            </span>
            <ManagedFileLink
              file={agreement.pricing_managed_file_detail}
              icon={<FileText size={12} />}
            >
              Pobierz cennik
            </ManagedFileLink>
          </div>
        ) : (
          <p className="text-sm text-gray-500 dark:text-gray-400">
            Brak zamieszczonego cennika.
            {canManageDoc && " Dodaj go przyciskiem „Edytuj”."}
          </p>
        )}
      </div>

      {/* Line items */}
      <LineItemsSection
        agreementId={agreement.id}
        lineItems={agreement.line_item_types}
        isDirector={isDirector}
      />

      {/* Automatic line items (dopisywane same przy pozycji wyzwalającej) */}
      <AutoLineItemsSection
        agreementId={agreement.id}
        lineItems={agreement.line_item_types}
        isDirector={isDirector}
      />

      {/* Verbal positions for AI valuation */}
      <VerbalRulesSection agreementId={agreement.id} isDirector={isDirector} />

      {/* Insurance / Polisa OC (§13) */}
      {canManageDoc && agreement.requires_insurance && (
        <div
          className={`mb-6 rounded-xl border p-4 ${
            agreement.insurance_status === "expired" ||
            agreement.insurance_status === "missing"
              ? "border-red-300 bg-red-50 dark:bg-red-900/20"
              : agreement.insurance_status === "expiring_soon"
                ? "border-amber-300 bg-amber-50 dark:bg-amber-900/20"
                : "border-green-200 bg-green-50 dark:bg-green-900/20"
          }`}
        >
          <h3 className="text-sm font-semibold text-gray-800 dark:text-gray-200 mb-3 flex items-center gap-2">
            <ShieldAlert
              size={15}
              className={
                agreement.insurance_status === "expired" ||
                agreement.insurance_status === "missing"
                  ? "text-red-500"
                  : agreement.insurance_status === "expiring_soon"
                    ? "text-amber-500"
                    : "text-green-600"
              }
            />
            Polisa OC wykonawcy
            {agreement.min_insurance_sum && (
              <span className="text-xs font-normal text-gray-500 dark:text-gray-400">
                — wymagana min. {formatAmount(agreement.min_insurance_sum)} PLN
              </span>
            )}
          </h3>

          {/* Versioned policy history */}
          {ocPolicies.length > 0 ? (
            <div className="mb-4 flex flex-col gap-2">
              {ocPolicies.map((p, idx) => (
                <div
                  key={p.id}
                  className={`rounded-lg border bg-white dark:bg-gray-800 p-3 text-sm ${
                    idx === 0
                      ? "border-[var(--color-primary)] dark:border-[var(--color-primary)]"
                      : "border-gray-200 dark:border-gray-700 opacity-70"
                  }`}
                >
                  <div className="flex items-center gap-3 flex-wrap">
                    {idx === 0 && <Badge color="blue">Aktualna</Badge>}
                    <span className="font-medium text-gray-900 dark:text-gray-100">
                      Ważna do:{" "}
                      {new Date(p.valid_until).toLocaleDateString("pl")}
                    </span>
                    <span className="text-gray-600 dark:text-gray-400">
                      Suma: {formatAmount(p.insurance_sum)} PLN
                    </span>
                    {p.policy_file_url && (
                      <a
                        href={p.policy_file_url}
                        target="_blank"
                        rel="noreferrer"
                        className="inline-flex items-center gap-1 text-[var(--color-primary)] dark:text-[var(--color-primary)] hover:underline text-xs"
                      >
                        <FileText size={12} /> Pobierz skan{" "}
                        <ExternalLink size={10} />
                      </a>
                    )}
                    <span className="text-xs text-gray-400 dark:text-gray-500 ml-auto">
                      {new Date(p.created_at).toLocaleDateString("pl")}
                      {p.uploaded_by_name && ` · ${p.uploaded_by_name}`}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-sm text-gray-500 dark:text-gray-400 mb-3">
              Brak wgranych polis OC.
            </p>
          )}

          {/* Director upload form */}
          <div className="bg-white dark:bg-gray-800 p-3">
            <p className="text-xs font-semibold text-gray-600 dark:text-gray-400 mb-2 flex items-center gap-1">
              <Upload size={12} /> Wgraj nową polisę OC
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 mb-2">
              <div>
                <label className="block text-xs text-gray-500 dark:text-gray-400 mb-0.5">
                  Data ważności *
                </label>
                <DatePicker
                  value={ocForm.valid_until}
                  onChange={(v) => setOcForm((f) => ({ ...f, valid_until: v }))}
                />
              </div>
              <div>
                <label className="block text-xs text-gray-500 dark:text-gray-400 mb-0.5">
                  Suma ubezpieczenia (PLN) *
                </label>
                <input
                  type="number"
                  min="0"
                  step="1000"
                  className="w-full rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-2 py-1 text-sm text-gray-900 dark:text-gray-100"
                  value={ocForm.insurance_sum}
                  onChange={(e) =>
                    setOcForm((f) => ({ ...f, insurance_sum: e.target.value }))
                  }
                />
              </div>
              <div>
                <label className="block text-xs text-gray-500 dark:text-gray-400 mb-0.5">
                  Plik polisy *
                </label>
                <FileDropZone
                  files={ocFile ? [ocFile] : []}
                  onFilesChange={(fs) => setOcFile(fs[0] ?? null)}
                  accept=".pdf,.jpg,.jpeg,.png"
                />
              </div>
            </div>
            {ocUploadError && (
              <p className="text-xs text-red-500 mb-1">{ocUploadError}</p>
            )}
            <Button
              size="sm"
              disabled={
                !ocFile ||
                !ocForm.valid_until ||
                !ocForm.insurance_sum ||
                uploadOcMutation.isPending
              }
              onClick={() => uploadOcMutation.mutate()}
            >
              {uploadOcMutation.isPending ? "Wgrywanie…" : "Prześlij polisę"}
            </Button>
          </div>
        </div>
      )}

      {/* Report template info */}
      {isDirector && (
        <div className="mb-6 bg-white dark:bg-gray-800 p-4">
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-sm font-semibold text-gray-800 dark:text-gray-200">
              Raporty tygodniowe
            </h3>
            <button
              onClick={() => setShowEdit(true)}
              className="flex items-center gap-1 text-xs text-[var(--color-primary)] dark:text-[var(--color-primary)] hover:underline"
            >
              <Pencil size={12} /> Edytuj
            </button>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3 text-sm">
            <div>
              <p className="text-xs text-gray-500 dark:text-gray-400 mb-0.5">
                Wymagane
              </p>
              <p className="font-medium">
                {agreement.requires_weekly_reports ? "Tak" : "Nie"}
              </p>
            </div>
            {agreement.requires_weekly_reports && (
              <>
                <div>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mb-0.5">
                    Typ raportu
                  </p>
                  <p className="font-medium">
                    {"Standardowy"}
                  </p>
                </div>
                {agreement.report_day_of_week != null && (
                  <div>
                    <p className="text-xs text-gray-500 dark:text-gray-400 mb-0.5">
                      Termin składania
                    </p>
                    <p className="font-medium">
                      {
                        ["Pn", "Wt", "Śr", "Cz", "Pt", "So", "Nd"][
                          agreement.report_day_of_week
                        ]
                      }
                      {agreement.report_due_time
                        ? ` godz. ${agreement.report_due_time}`
                        : ""}
                    </p>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      )}

      {/* Konwersacje wewnętrzne — dyrektorska i zarządu (jak przy przedsięwzięciu) */}
      {user && (isDirectorRole || isCeo) && (
        <div ref={conversationsRef} className="scroll-mt-4">
          <AgreementConversations
            agreementId={agreement.id}
            currentUserId={user.id}
            isDirector={isDirectorRole}
            isCeo={isCeo}
          />
        </div>
      )}

      {/* Historia umowy — kto ją założył, zmieniał i co robił z cennikiem */}
      {id && <AgreementHistorySection agreementId={id} />}

      {/* Tabs */}
      <div className="flex border-b border-gray-200 dark:border-gray-700 mb-4">
        <button
          className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
            activeTab === "history"
              ? "border-[var(--color-primary)] text-[var(--color-primary)] dark:text-[var(--color-primary)]"
              : "border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700"
          }`}
          onClick={() => setActiveTab("history")}
        >
          Historia zamówień
        </button>
        <button
          className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
            activeTab === "invoices"
              ? "border-[var(--color-primary)] text-[var(--color-primary)] dark:text-[var(--color-primary)]"
              : "border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700"
          }`}
          onClick={() => setActiveTab("invoices")}
        >
          Historia FV
        </button>
        <button
          className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
            activeTab === "penalties"
              ? "border-[var(--color-primary)] text-[var(--color-primary)] dark:text-[var(--color-primary)]"
              : "border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700"
          }`}
          onClick={() => setActiveTab("penalties")}
        >
          Kary umowne
          {penaltyRecords.length > 0 && (
            <span className="ml-1.5 text-xs bg-red-100 text-red-700 rounded-full px-1.5 py-0.5">
              {penaltyRecords.length}
            </span>
          )}
        </button>
      </div>

      {activeTab === "history" && id && (
        <OrderHistorySection agreementId={id} />
      )}

      {activeTab === "invoices" && id && (
        <InvoiceHistorySection agreementId={id} />
      )}

      {activeTab === "penalties" && id && (
        <PenaltiesTab
          agreementId={id}
          agreement={agreement}
          penaltyRecords={penaltyRecords}
          isDirector={isDirector}
        />
      )}

      {/* Delete confirmation */}
      {showDelete && (
        <DeleteAgreementDialog
          agreement={agreement}
          open={showDelete}
          onClose={() => setShowDelete(false)}
        />
      )}

      {/* Edit modal */}
      {showEdit && (
        <EditAgreementModal
          agreement={agreement}
          open={showEdit}
          onClose={() => setShowEdit(false)}
          canManageDoc={canManageDoc}
        />
      )}
    </div>
  );
}
